/**
 * RACING SLOP — the table.
 *
 * The engine resolves a whole race the moment it is set up. What this file does
 * is walk a cursor through the turns it produced, so that "advance a turn" means
 * "show me the next thing that already happened" and never "roll again". Every
 * number on screen is read off `RaceResult`; the UI owns layout, motion and
 * nothing else.
 *
 * ===========================================================================
 * THE SHELL OWNS THE CAREER. THAT IS THE POINT OF THIS FILE.
 * ===========================================================================
 *
 * Howard, 17 Aug 2026:
 *
 *   "youve made it so you cant run a season/career week by week… The problem is
 *    even then on the career page all you can do is roll the seed now or
 *    simulate an entire season at once. It cant be like that death loop."
 *
 * The loop was structural. A season lived inside whichever screen ran it, so
 * leaving that screen was losing it — which is why Save had nothing to save, the
 * record book had nothing to print, a loaded league had nowhere to go, and the
 * Set up tab could not offer race 2. All four of those are one bug: state in
 * the wrong place.
 *
 * So the SEASON and the CAREER live here, above every screen:
 *
 *   • `season`   — the live `SeasonState` (`domain/seasonPlay.ts`) plus the
 *     roster, garage and season number it belongs to. `SeasonPlay.tsx` plays
 *     race N off it, the shell shows the race, and the state comes back one race
 *     further on. Thirty-six times, then `finishSeason`.
 *   • `career`   — the `CareerSeason` a finished season settles into, whether it
 *     was played race by race or simulated on the Career tab. `AccountBar` saves
 *     THIS, `PrintSheet` prints THIS, `CareerScreen` displays THIS.
 *
 * A screen that runs something hands it up; nothing that matters is held below
 * this file.
 *
 * THE SHELL ALSO OWNS FOUR THINGS THAT ARE NOT THE RACE, and they are here
 * rather than on a screen because they have to be true on every screen:
 *
 *   • THE ACCOUNT BAR (`AccountBar`) — register, sign in, and the one league
 *     slot. It is a strip, never a door: a signed-out visitor keeps the entire
 *     game and loses exactly one thing, coming back to the same career.
 *   • THE CLOUD NUDGE (`CloudNudge`) — "load before you play" at a new
 *     location, and a plain statement of what a load would destroy when there
 *     is unsaved work in this browser.
 *   • THE AI NAME POOL (`nameStore`) — fetched once per browser, applied to a
 *     field before the season starts (or at the green for an exhibition), and
 *     reported in words either way.
 *   • THE PRINTABLE RECORD BOOK (`PrintSheet`) — a top-level view, because a
 *     record book is a document and not a panel inside a season screen.
 *
 * NOTHING IN THIS FILE SAVES ANYTHING ON ITS OWN. Howard, 17 Aug 2026: *"Save
 * and load manually is the key to keeping progress straight without burning up
 * the I/O at supabase on auto saves."* The only writes this shell makes are two
 * local markers — an account id it has seen, and a timestamp saying this browser
 * has unsaved work — and both exist so the nudge can tell the truth. See
 * `markLocalProgress` in `components/cloudNudge.ts`.
 *
 * `account.css` and `print.css` are NOT imported here: `AccountBar.tsx` and
 * `PrintSheet.tsx` each import their own stylesheet at the top of the file, so
 * the styles arrive with the component and there is no second step to forget.
 * A duplicate import here would be dead weight, not insurance.
 */

import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildStartingGrid, runRace } from './domain/race.ts';
import type { RaceResult, RaceSetup as EngineRaceSetup } from './domain/race.ts';
import DiceTable from './components/DiceTable.tsx';
import { buildField, DEFAULT_ROSTER_SIZE } from './domain/field.ts';
import type { Team } from './domain/field.ts';
import { MAX_STARTING_FIELD } from './domain/raceWeek.ts';
import type { RaceWeekResult } from './domain/raceWeek.ts';
import { trackCard } from './domain/turn.ts';
import type { RaceState } from './domain/types.ts';
import RaceBoards from './components/RaceBoards.tsx';
import BoothCall from './components/BoothCall.tsx';
import { MomentPanel } from './components/MomentPanel.tsx';
import { ArtHistory, conditionForRace } from './domain/art/select.ts';
import { loadLibrary, type StoredArtImage } from './lib/artStore.ts';
import TurnControls from './components/TurnControls.tsx';
import EventFeed from './components/EventFeed.tsx';
import TurnDice from './components/TurnDice.tsx';
import { drawsAtFinish } from './domain/diceTrace.ts';
import { raceViewAt } from './presentation/raceView.ts';
import RaceDiceWorkbench, { type PendingTableTurn } from './components/RaceDiceWorkbench.tsx';
import { readTablePreferences, saveTablePreferences } from './presentation/raceDice.ts';
import type { FeedSelection } from './components/EventFeed.tsx';
import RaceSetupScreen from './components/RaceSetup.tsx';
import { scenario2025 } from './domain/scenario2025.ts';
import type { RaceConfig, SeasonStart } from './components/RaceSetup.tsx';
import SeasonPlay from './components/SeasonPlay.tsx';
import { seasonStartingGrid, settleSeason } from './components/seasonPlayModel.ts';
import {
  beginSeason,
  finishSeason,
  isSeasonComplete,
  nextRaceNumber,
  runSeasonRace,
  type SeasonRaceStep,
  type SeasonState,
} from './domain/seasonPlay.ts';
import { REGULAR_SEASON_RACES, SEASON_RACES } from './domain/schedule.ts';
import { ownerMap } from './domain/finance/seasonFinance.ts';
import type { RaceFinanceResult } from './domain/finance/raceFinance.ts';
import type { DriverCard } from './domain/types.ts';
import RaceResults from './components/RaceResults.tsx';
import Payday from './components/Payday.tsx';
import CareerScreen from './components/CareerScreen.tsx';
import DriverDossier from './components/DriverDossier.tsx';
import TrackContent from './components/TrackContent.tsx';
import { flagFor } from './components/boardModel.ts';
import { runRaceFinance, newFinanceState, profileFromCard, financeTeamId } from './domain/finance/index.ts';
import AccountBar, { SAVE_BUTTON_ID } from './components/AccountBar.tsx';
import AiKeyPanel from './components/AiKeyPanel.tsx';
import SettingsPanel from './components/SettingsPanel.tsx';
import { Cite, ErrorNote, type FriendlyError } from './components/Cite.tsx';
import { friendlyError } from './lib/prefs.ts';
import PaperModal from './components/PaperModal.tsx';
import SeasonStrip from './components/SeasonStrip.tsx';
import { notebook, type Notebook } from './domain/paper/notebook.ts';
import { collectSources } from './components/statsModel.ts';
import { paperFacts } from './domain/paper/paperFacts.ts';
import { readThePaper, type PaperArticle } from './lib/paper.ts';
import FrontDoor, { NextSeasonCard } from './components/FrontDoor.tsx';
import WinterChapters from './components/chapters/WinterChapters.tsx';
import LeagueCreate, { type LeagueStart } from './components/LeagueCreate.tsx';
import ExhibitionHandoff from './components/ExhibitionHandoff.tsx';
import type { LeagueSettingsView } from './components/SettingsPanel.tsx';
import {
  AFTER_BIG_HOLD_MS,
  defaultPace,
  MOMENT_WORDS,
  paceMs as msForPace,
  PEEK_HOLD_MS,
  planCommissionerStep,
  planMomentStep,
  readPacePref,
  revealCamCall,
  savePacePref,
  visibleTurnState,
  type MomentReason,
  type PaceKey,
  type Peek,
} from './presentation/bigMoments.ts';
import {
  DEFAULT_LEAGUE_SETTINGS,
  normaliseSettings,
  seasonOptionsFor,
  settingsOfCareer,
  settingsOfSeasonOptions,
  type LeagueSettings,
} from './lib/leagueSettings.ts';
import { progressKey, unsavedWarning } from './lib/unsavedWork.ts';
import { randomSeed } from './lib/seed.ts';
import type { RaceOptions } from './domain/race.ts';
// D-EZ-102 — story screens (Victory Lane, the moment cards, the booth's memory).
import VictoryLane from './components/story/VictoryLane.tsx';
import RaceMomentCard from './components/story/RaceMomentCard.tsx';
import { leagueSeasons, seasonContextBefore } from './domain/story/league.ts';
import { boothLeagueFor } from './domain/booth/league.ts';
import { stakesNotes } from './domain/booth/notes.ts';
import { bigTurns, holdTurns } from './domain/story/raceStory.ts';
// D-EZ-104/105 — stakes before the dice; the commissioner rolls the big moments.
import {
  afterLine,
  commissionerRace,
  stakesRulesFrom,
  type RaceFrame,
  type StakesContext,
  type TableMoment,
} from './presentation/stakes/stakes.ts';
/*
  CODE-SPLIT — 4 Oct 2026. The owner's three tools and the printable record
  book load only when opened, so every other visitor's first page carries none
  of them. See the bundle sizes in the commit that made this change.
*/
const ArtTagger = lazy(() => import('./components/ArtTagger.tsx').then((m) => ({ default: m.ArtTagger })));
const LogoManager = lazy(() => import('./components/LogoManager.tsx'));
const AdminPanel = lazy(() => import('./components/AdminPanel.tsx'));
const PrintSheet = lazy(() => import('./components/PrintSheet.tsx'));
const loadingScreen = <p className="dim wrap">Loading…</p>;
import RebuildSeasons from './components/RebuildSeasons.tsx';
import CloudNudge from './components/CloudNudge.tsx';
import { applyNamePool } from './domain/driverGen.ts';
import { ensureNamePool, namesForField, type EnsuredPool } from './lib/nameStore.ts';
import { newLeagueId, peekSlot, type LoadedCareer, type LoadedLeague, type SlotSummary } from './lib/leagueSlot.ts';
import { foldSettled, freshShelf, shelfFromArchive, type Shelf } from './lib/leagueShelf.ts';
import { backupLeague, backupOwner, describeBackup, listBackups, restoreBackup, type BackupSummary } from './lib/localBackup.ts';
import type { LiveSeason } from './lib/seasonSlot.ts';
import { feudHistories } from './domain/feudHistory.ts';
import { currentAccount, isOwner, onAccountChange, type Account } from './lib/auth.ts';
import type { CareerSeason } from './lib/careerRunner.ts';
import { emptyCareerCarry, type CareerCarry } from './domain/careerCarry.ts';
import {
  accountSeenBefore,
  decideCloudNudge,
  markLocalProgress,
  markLocalSaved,
  readLocalProgress,
  rememberAccount,
  type CloudState,
  type LocalState,
  type NudgeKind,
} from './components/cloudNudge.ts';

// 'dice' is D-018's hand-rolled race: the setup screen has been left, the
// engine is running, and it is stopped on a die waiting for Howard's table.
// 'setup' is HOME (D-EZ-103): the front door, league creation, the season home
// or an exhibition — which one is `Home` below.
type View = 'setup' | 'race' | 'career' | 'content' | 'art' | 'logos' | 'print' | 'dice' | 'people';

/**
 * D-EZ-103 — WHAT HOME SHOWS when no season is being played.
 *
 *   door        the front door (no league yet) or the next-season card
 *   create      league creation — the one way a season begins
 *   exhibition  one race at a track of the player's choosing
 *
 * A live season always wins: Home is then the season home.
 */
type Home = 'door' | 'create' | 'exhibition';

interface LiveRace {
  config: RaceConfig;
  result: RaceResult;
  /** The grid as it lined up, before a turn was run. */
  start: RaceState;
  /** The garage that entered it — carried for team names and liveries only. */
  teams: Team[];
  /** The week that set the grid. Payday reads its chart codes for sponsorship. */
  week: RaceWeekResult;
  /**
   * A SEASON race's real money, settled by the season's own finance track.
   *
   * Null for an exhibition, which has no season books and gets the standing-start
   * purse computed below instead. This is the difference between a Payday that
   * belongs to a championship — carrying the year's sponsorships, blackouts and
   * last week's gate — and one priced as though the sport began on Sunday.
   */
  seasonFinance: RaceFinanceResult | null;
}

/**
 * A CAREER IN PROGRESS — the live season, plus what the winter will need.
 *
 * `state` is the only part that moves. The other three are fixed when the season
 * begins and are exactly what `runOffseason` reads at the end of it: the roster
 * the season was RUN with (not the re-rated one), the garage that entered it, and
 * the seasons-run counter carried out of the previous winter.
 */
/**
 * A roster and garage carried out of a previous winter, or out of a saved slot.
 * Exactly `nextSeasonOptions()`'s inputs plus the season number they belong to.
 */
interface Carried {
  roster: DriverCard[];
  teams: Team[];
  seasonNumber: number;
  seasonsRun?: Record<string, number>;
  /** D-EZ-91/94 — contracts, banks, feuds, injuries carried out of the winter. */
  carry?: CareerCarry;
}

/**
 * A CAREER IN PROGRESS — the live season, plus what the winter will need.
 *
 * IT IS `LiveSeason` FROM `lib/seasonSlot.ts`, not a shape of its own, and that
 * is deliberate: the thing on the workbench and the thing that goes in the slot
 * must be the same object, or "save the season in progress" would mean copying
 * fields from one shape into another and one of them would eventually be
 * forgotten. A load hands one of these straight back (`onLeagueLoaded`).
 */
type CareerLine = LiveSeason;

const DEFAULT_CONFIG: RaceConfig = {
  trackId: 'daytona-2014',
  totalTurns: trackCard('daytona-2014').turns,
  seed: 424242,
  fieldSize: 43,
  rosterSize: DEFAULT_ROSTER_SIZE,
};

/**
 * What the name pool did to THIS field, counted rather than claimed.
 *
 * `nameStore.ts`: *"a fallback the user cannot see is indistinguishable from a
 * bug"*. So every one of these numbers reaches the screen, including the
 * uncomfortable ones — a field where 40 of 53 cars took a pool name is
 * reported as exactly that and never as "AI names".
 */
interface Naming {
  /** Cards that took a name the model wrote. */
  renamed: number;
  /** Cards that kept the name the built-in vocabulary gave them. */
  keptBuiltIn: number;
  /** Cards the deal could not cover at all, because the pool is smaller. */
  shortfall: number;
  /** `EnsuredPool.detail` — why the pool is what it is, in one sentence. */
  poolDetail: string;
  /** `NameAssignment.detail` — what this seed's deal did, in one sentence. */
  dealDetail: string;
}

// ---------------------------------------------------------------------------
// BOOT — three questions asked once, off the render path
// ---------------------------------------------------------------------------

interface Boot {
  account: Account | null;
  cloud: CloudState;
  local: LocalState;
  /** False means "new location" in Howard's sense — see `accountSeenBefore`. */
  seenHereBefore: boolean;
  pool: EnsuredPool;
}

/**
 * Ask `peekSlot` what is in the cloud, and turn its answer into the four-state
 * fact the nudge reasons about.
 *
 * `peekSlot` reads five label columns and never the payload, which is what
 * makes it acceptable to call on every visit: this is the cheapest question the
 * database can be asked, and asking it is how the shell knows whether the
 * player has a season somewhere else. Signed out, it answers locally without a
 * request at all.
 */
async function readCloud(account: Account | null): Promise<CloudState> {
  if (!account) return { state: 'signed-out' };
  try {
    const r = await peekSlot();
    if (!r.ok) {
      // "Not signed in" between the session read and this call is a race, not
      // an error, and it is reported as the state it actually is.
      if (r.code === 'not-signed-in') return { state: 'signed-out' };
      return { state: 'unknown', reason: r.message };
    }
    if (!r.summary) return { state: 'empty' };
    return {
      state: 'save',
      save: {
        leagueName: r.summary.leagueName,
        seasonNumber: r.summary.seasonNumber,
        // The nudge says a different sentence for a season in progress than for
        // a finished one — "your season is part-run in the cloud" is an urgent
        // instruction and "your saved league is waiting" is a reminder — so the
        // kind and the race count are passed through rather than flattened.
        kind: r.summary.kind,
        racesRun: r.summary.racesRun,
        savedAt: r.summary.savedAt,
      },
    };
  } catch (e) {
    return {
      state: 'unknown',
      reason: `Your saved league could not be read (${String((e as Error)?.message ?? e)}).`,
    };
  }
}

/**
 * "HAS THIS BROWSER BEEN HERE BEFORE?" — ASKED ONCE PER VISIT, PER ACCOUNT.
 *
 * The marker this reads is written the first time an account is seen, so the
 * question destroys its own answer: ask it twice and the second answer is
 * always "yes". Two things ask it — the boot sequence and the auth listener,
 * which Supabase fires with `INITIAL_SESSION` moments after boot begins — and
 * whichever lost that race would have downgraded a first-visit warning to a
 * reminder, silencing the exact case Howard asked to be pushed hardest.
 *
 * So the verdict is memoised per account id (and `''` for signed out) for the
 * life of the page. Within one visit the answer cannot flip, which is also the
 * truthful behaviour: signing out and back in is not arriving somewhere new.
 */
const seenVerdicts = new Map<string, boolean>();

function seenBeforeThisVisit(account: Account | null): boolean {
  const key = account?.id ?? '';
  const cached = seenVerdicts.get(key);
  if (cached !== undefined) return cached;
  const verdict = accountSeenBefore(account?.id ?? null);
  seenVerdicts.set(key, verdict);
  // Recorded only after the verdict is fixed, so this browser is "known" from
  // the next visit onward and not from the next question onward.
  if (account) rememberAccount(account.id);
  return verdict;
}

/**
 * The one boot sequence, memoised at module scope.
 *
 * MEMOISED BECAUSE OF STRICT MODE, and because of what the last step costs.
 * React's development Strict Mode mounts, unmounts and remounts every component
 * to shake out effects that are not idempotent; without this, a first-time
 * visitor would send TWO `/api/names` batches, pay for both, and keep one. A
 * module-level promise means the second caller waits on the first answer.
 *
 * THE ORDER IS THE ARGUMENT. `ensureNamePool` is told whether a career is under
 * way, and the honest answer to that question needs the cloud slot and the
 * local marker first — so those are read before the pool is asked for, not
 * alongside it.
 */
let bootOnce: Promise<Boot> | null = null;

async function boot(): Promise<Boot> {
  if (!bootOnce) bootOnce = runBoot();
  return bootOnce;
}

async function runBoot(): Promise<Boot> {
  const account = await currentAccount().catch(() => null);
  const cloud = await readCloud(account);
  const local = readLocalProgress();
  const seenHereBefore = seenBeforeThisVisit(account);

  /**
   * REQUIREMENT 4 OF THE NAME POOL, ANSWERED WITH EVIDENCE.
   *
   * Howard: *"Only the first loading when someone visits the site without a
   * career going."* `ensureNamePool` cannot see a career and deliberately does
   * not import one — it is told. This shell's honest answer is: a saved league
   * in the cloud is a career; unsaved work recorded in this browser is a
   * career; and a slot we could not read might be one, so it counts as one.
   * Only a visitor with no save anywhere and no local work gets new names,
   * which is precisely the case in the requirement.
   *
   * ⚠ ONE GAP, STATED: a season run in THIS session is not visible to this
   * question, because the boot sequence runs once before anything has been
   * played. A player who plays and then reloads with no cloud save is covered by
   * the local marker (`markLocalProgress`, written when a race is run and when a
   * season starts), so the only case left is a reload inside the first few
   * seconds of a career. The cost of getting it wrong is a changed vocabulary
   * for the NEXT league, never a renamed champion: a pool only ever names a
   * field before it is dealt.
   */
  const careerInProgress =
    cloud.state === 'save' || cloud.state === 'unknown' || local.state === 'progress';

  const pool = await ensureNamePool({ careerInProgress });
  return { account, cloud, local, seenHereBefore, pool };
}

export default function App() {
  const [view, setView] = useState<View>('setup');
  const [home, setHome] = useState<Home>('door');
  /** D-EZ-103 — remounts the exhibition screen; `startAtWeek` goes straight to race week. */
  const [exhibition, setExhibition] = useState<{ key: number; startAtWeek: boolean }>({ key: 0, startAtWeek: false });
  /**
   * D-EZ-103 — THE LEAGUE'S SETTINGS FOR THE NEXT SEASON IT STARTS. Set when a
   * league is created or loaded; editable in Settings "for next season"; saved
   * with the league. A season under way keeps its own in `state.options`.
   */
  const [nextSettings, setNextSettings] = useState<LeagueSettings>(DEFAULT_LEAGUE_SETTINGS);
  /** D-EZ-103 — how far the hand-rolled race has got, for the "Your dice" tab. */
  const [handProgress, setHandProgress] = useState<{ turn: number; faces: number } | null>(null);
  const [config, setConfig] = useState<RaceConfig>(DEFAULT_CONFIG);
  const [race, setRace] = useState<LiveRace | null>(null);
  /**
   * D-018 — the hand-rolled race in progress, if there is one.
   *
   * The setup that will be raced, and the same post-race tail a seeded race
   * runs. Held rather than passed because the dice table lives for as long as
   * it takes a human to roll nine hundred dice, and nothing else on this screen
   * should be re-created underneath it.
   */
  const [handDice, setHandDice] = useState<{
    setup: EngineRaceSetup;
    /** D-EZ-103 — the exhibition's house rules, so the hand-rolled race runs the same rules. */
    options: RaceOptions;
    complete: (result: RaceResult) => void;
  } | null>(null);
  /**
   * D-018 for a SEASON race — the state the race will be rolled against.
   *
   * A separate field from `handDice` because it is a different session class
   * (`ManualSeasonRace`, which owns the whole race including the week) and
   * because what comes back at the flag is a `SeasonRaceStep`, not a
   * `RaceResult`. One field holding either would have to be unwrapped at every
   * use; two fields are read at exactly one place each.
   */
  const [seasonDice, setSeasonDice] = useState<{ state: SeasonState; raceNumber: number } | null>(null);
  /**
   * A WEEKEND THE PLAYER HAS JUST ROLLED, on its way back to the season screen.
   *
   * It does NOT go straight to the tape. `SeasonPlay` tells the week's story and
   * shows the grid first, exactly as it does for a seeded weekend — which is
   * the whole point now that the WEEK is hand-rolled as well as the race: the
   * player rolled that grid and has not seen it yet. Cleared the moment the
   * green is dropped, so a second hand-rolled race cannot adopt the first one.
   */
  const [rolledWeekend, setRolledWeekend] = useState<SeasonRaceStep | null>(null);
  // D-EZ-101 — a sentence a player can act on, with the raw text behind the
  // "Show rulebook citations" switch (see `friendlyError`).
  const [error, setError] = useState<FriendlyError | string | null>(null);
  // The art library is loaded once and held: a race asks for a picture on
  // almost every one of ~130 events, so a query per moment would be absurd.
  const [artLibrary, setArtLibrary] = useState<readonly StoredArtImage[]>([]);

  /**
   * ONE ArtHistory FOR THE WHOLE SITTING, not one per race.
   *
   * `maxPerSeason` is a rarity-tier rule and it can only mean something if the
   * history outlives a single race — a fresh ArtHistory per race made the cap
   * unreachable by construction, so a legendary photo was capped per race and
   * never per season. Held in a ref so replaying the tape does not reset it and
   * a re-render does not rebuild it.
   *
   * `start()` calls `startRace()` on it: the per-race "don't repeat this
   * picture" memory clears, the per-season counts do not. That is the whole
   * distinction the class exists to draw.
   */
  const artHistory = useRef(new ArtHistory());

  // ---- the shell: account, cloud slot, names ------------------------------

  /**
   * THE CAREER THIS SHELL CAN REACH — and it reaches every one there is.
   *
   * `AccountBar` saves a `CareerSeason`, `PrintSheet` prints one, `CareerScreen`
   * displays one. All three read these two values, so there is exactly one
   * career in the application and no screen has a private copy.
   *
   * TWO WAYS ONE GETS HERE, and both are a season that was actually run:
   *
   *   • played race by race — `settleSeason` folds the money and the winter over
   *     the `SeasonResult` `finishSeason` produced (see `settle` below);
   *   • simulated on the Career tab — `runCareerSeason` returns one whole, and
   *     that screen hands it up through `onSeason`.
   *
   * STILL NOT FABRICATED FROM A LOAD. `LoadedLeague.restored` carries a roster,
   * a race list and two ledgers, but not the season fold, the finance result or
   * the winter a `CareerSeason` is made of. So a load does not put a season
   * here; it puts the CARRY into a new season, which is a thing that can be
   * played (`continueLoaded`).
   */
  const [reachableCareer, setReachableCareer] = useState<CareerSeason | null>(null);
  const [reachableHistory, setReachableHistory] = useState<CareerSeason[]>([]);
  /**
   * D-EZ-99 — WHICH LEAGUE THE SHELF ABOVE BELONGS TO. A new league gets a new
   * id and an empty shelf; a load brings its own id and only its own seasons;
   * a settled season is folded in only if it is the same league. Leagues never
   * merge (`lib/leagueShelf.ts`), and every save and device copy carries it.
   */
  const [leagueId, setLeagueId] = useState<string>(() => newLeagueId());
  /** Bumped after each automatic copy to this device, so the account list refreshes. */
  const [backupTick, setBackupTick] = useState(0);
  /** D-EZ-103 — `progressKey` of what the device copy or an account save last protected. */
  const [protectedKey, setProtectedKey] = useState<string | null>(null);
  /** The workbench's key as of the last render, for callbacks that must not re-create. */
  const workbenchKeyRef = useRef<string | null>(null);
  const putShelf = useCallback((shelf: Shelf) => {
    setLeagueId(shelf.leagueId);
    setReachableCareer(shelf.career);
    setReachableHistory(shelf.history);
  }, []);

  /**
   * THE LIVE SEASON. Null when nobody is running one.
   *
   * Here rather than in `RaceSetup.tsx` or `CareerScreen.tsx` for one reason: a
   * season has to survive leaving the screen that started it, or race 2 does not
   * exist. Everything else about race-by-race play follows from that.
   */
  const [season, setSeason] = useState<CareerLine | null>(null);
  /** True while the winter is being run — a second or so of arithmetic. */
  const [settling, setSettling] = useState(false);
  /** D-EZ-59 — how far a fast-forward has got, or null when nothing is running. */
  const [fastForward, setFastForward] = useState<{ done: number; total: number; label: string } | null>(
    null,
  );
  /**
   * The league's NAME, shared by the account bar (which files the save under it)
   * and the career screen (which used to own it privately, so Save filed every
   * league as "RASCAL").
   */
  // D-EZ-103 — named when the league is created; blank until there is one.
  const [leagueName, setLeagueName] = useState('');

  /**
   * WHO IS SIGNED IN — held here for exactly one reason: the Art library tab.
   *
   * Howard, 17 Aug 2026: *"users other than me (Howard@roundtablerealty.com)
   * cannot see the art gallery as they dont have edit options like that. Please
   * remove that from the dashboard for anyone else."*
   *
   * Null means signed out, which is the normal state of this app and is not the
   * owner. Nothing else on any screen is gated on this value — the account bar
   * is still "a strip, never a door".
   */
  const [account, setAccount] = useState<Account | null>(null);
  /** D-EZ-103 — the newest league kept on this device, offered as "Continue" at the front door. */
  const [deviceLeague, setDeviceLeague] = useState<BackupSummary | null>(null);
  const [resumeError, setResumeError] = useState<string | null>(null);

  /** Null until boot has looked. "We have not looked" is not "nothing is there". */
  const [cloud, setCloud] = useState<CloudState | null>(null);
  const [local, setLocal] = useState<LocalState>({ state: 'none' });
  const [seenHereBefore, setSeenHereBefore] = useState(false);
  const [pool, setPool] = useState<EnsuredPool | null>(null);
  /** What the pool did to the field currently on the tape. Null before a race. */
  const [naming, setNaming] = useState<Naming | null>(null);
  /** Dismissal is per SITUATION: a new kind of warning is a new warning. */
  const [dismissedNudge, setDismissedNudge] = useState<NudgeKind | null>(null);
  /**
   * The slim cloud strip, dismissed for the whole SESSION — not just this kind
   * of message and not just this screen (D-EZ-99 / readability audit).
   */
  const [nudgeOff, setNudgeOff] = useState<boolean>(() => {
    try {
      return window.sessionStorage.getItem('rwbr.cloudstrip.dismissed') === '1';
    } catch {
      return false;
    }
  });
  /**
   * The last FINISHED season pulled out of the slot, so the shell can say what
   * came back and offer the one thing it is good for (season N+1 off its winter).
   *
   * `LoadedCareer` and not `LoadedLeague`: a loaded season in progress does not
   * belong here at all — it goes on the workbench as `season` — and typing this
   * as the union would have let the "continue into season N+1" panel appear over
   * a championship the player had just picked up mid-flight.
   */
  const [loaded, setLoaded] = useState<LoadedCareer | null>(null);

  /**
   * `start` reads the pool through a ref rather than through state.
   *
   * `ensureNamePool` is asynchronous and `start` is not — it is called from a
   * click deep inside the race-week screen and must finish in that click. A ref
   * gives it whatever the boot has settled on by then without making `start`
   * async, without a stale closure, and without rebuilding the callback every
   * time the pool state changes. Before the boot lands the ref is null, which
   * `namesForField` already handles: the field keeps its built-in names.
   */
  const poolRef = useRef<EnsuredPool | null>(null);
  /** The account bar, so the nudge can put it in front of the player. */
  const accountRef = useRef<HTMLDivElement | null>(null);
  const [flagAccount, setFlagAccount] = useState(false);

  const [cursor, setCursor] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [tablePreferences, setTablePreferences] = useState(readTablePreferences);
  const [tableSession, setTableSession] = useState<PendingTableTurn|null>(null);
  const activeTable = useRef<PendingTableTurn|null>(null);
  const tableSerial = useRef(0);
  useEffect(() => { saveTablePreferences(tablePreferences); }, [tablePreferences]);
  useEffect(() => {
    activeTable.current = null;
    setTableSession(null);
  }, [race]);
  useEffect(() => {
    if (view === 'race') window.scrollTo({top:0,behavior:'instant'});
    if (view !== 'race') {
      activeTable.current = null;
      setTableSession(null);
      setPlaying(false);
    }
  }, [view]);

  /**
   * D-EZ-103 — THE PACE. A remembered choice (per browser) wins; otherwise a
   * season race opens on Big moments and an exhibition on Race pace.
   */
  const [pacePref, setPacePref] = useState<PaceKey | null>(() => readPacePref());
  const pace: PaceKey = defaultPace(pacePref, Boolean(race?.config.seasonRaceNumber));
  const changePace = useCallback((next: PaceKey) => {
    setPacePref(next);
    savePacePref(next);
  }, []);
  /**
   * D-EZ-103 — BIG MOMENTS: the race cam call already put on screen for the
   * next turn (gate 2 of `presentation/bigMoments.ts`), and the big moment
   * being rolled on the table right now.
   */
  const [peek, setPeek] = useState<Peek | null>(null);
  const [moment, setMoment] = useState<{ turn: number; reason: MomentReason } | null>(null);
  /** The last turn Big moments rolled on the table, so the board holds after it. */
  const lastBig = useRef(0);
  const [selected, setSelected] = useState<FeedSelection | null>(null);
  const [lit, setLit] = useState<readonly string[]>([]);
  /** UI-008 — whose dossier is open over the boards. */
  const [dossier, setDossier] = useState<string | null>(null);

  /**
   * THE STOCK CAR DISPATCH — D-EZ-50.
   *
   * Howard, 18 Aug 2026: *"an after race newspaper article that encompasses the
   * latest race, the season and a little bit of career… It shows in a modal
   * after every race and at the conclusion of the season."*
   *
   * `shownFor` is what stops the modal reappearing every time this component
   * re-renders at the checkered flag. It is a ref rather than state on purpose:
   * marking an edition as seen must not itself cause a render, or the effect
   * that reads it runs again before the mark has landed.
   */
  /**
   * D-EZ-73 (F5) — WHICH SHELL PANEL IS OPEN, and null for neither.
   *
   * One at a time on purpose: they are both settings and two open forms above
   * the game is the standing chrome this replaced.
   */
  const [shellPanel, setShellPanel] = useState<'account' | 'ai' | 'settings' | null>(null);
  /** F6 — the season the record book opens at, driven by the strip's chips. */
  const [bookSeason, setBookSeason] = useState<number | null>(null);

  const [paper, setPaper] = useState<{
    article: PaperArticle | null;
    reason: string | null;
    standing: string;
    loading: boolean;
    /** D-EZ-70 — the short column, built here with no model. */
    notes: Notebook | null;
    /** "Vol. 3, No. 14". */
    volume: string;
    /** So "write it again" knows which edition to buy. */
    of: { seasonNumber: number; raceNumber?: number };
  } | null>(null);

  /**
   * WRITE (OR FETCH) ONE EDITION AND PUT IT ON SCREEN.
   *
   * `readThePaper` cannot fail: it returns a cached edition, a model's, or one
   * the game wrote itself, and the modal renders all three identically apart
   * from one honest line at the foot. So there is no error branch here — the
   * only state this can be in is "not written yet", which is the press running.
   */
  const openPaper = useCallback(
    async (of: { seasonNumber: number; raceNumber?: number }, fresh = false) => {
      const sources = collectSources({
        history: reachableHistory,
        career: reachableCareer,
        live: season,
      });
      const facts = paperFacts({
        leagueName,
        sources,
        seasonNumber: of.seasonNumber,
        raceNumber: of.raceNumber,
      });
      // No facts is not an error state and must not open an empty modal: it is
      // a season with nothing in it yet, and there is nothing to report.
      if (!facts) return;

      const standing =
        of.raceNumber === undefined
          ? `Season ${of.seasonNumber}`
          : `Race ${of.raceNumber} of ${facts.racesInSeason}`;

      /*
        D-EZ-70 — THE NOTEBOOK AND THE VOLUME ARE READY BEFORE THE ARTICLE IS.

        Both are pure functions of the facts already in hand, so they are on
        screen while the press is still running and they are the same whether the
        article that lands beside them was bought or written here.
      */
      const notes = notebook(facts);
      const volume = `Vol. ${facts.season}, No. ${facts.racesRun}`;

      setPaper({ article: null, reason: null, standing, notes, volume, loading: true, of });
      const outcome = await readThePaper(facts, { fresh });
      setPaper({
        article: outcome.article,
        reason: outcome.reason,
        standing,
        notes,
        volume,
        loading: false,
        of,
      });
    },
    [reachableHistory, reachableCareer, season, leagueName],
  );

  /**
   * The race starts from the WEEK, not from the field: the grid is race week's
   * finishing order, and the TV list, the feuds, the fume list and the chips
   * earned in qualifying all travel with it (RWEG/p.2-4). The garage is rebuilt
   * off the same seed for one thing only — team names and liveries.
   */
  const start = useCallback((next: RaceConfig, week: RaceWeekResult) => {
    try {
      const field = buildField(next.seed, { size: next.rosterSize });

      /**
       * THE AI NAMES, DEALT FOR THIS SEED.
       *
       * Three lines, and every one of them is a decision made somewhere else:
       * `namesForField` permutes the stored pool for this seed — it cannot
       * generate, extend or recombine a name — and `applyNamePool` substitutes
       * whole names onto cards whose ratings were rolled before any of this ran
       * and are not touched by it. With no pool the deal is empty, the field
       * keeps the names `buildField` gave it, and the game behaves exactly as
       * it always has.
       *
       * ⚠ THE FIELD IS RENAMED AT THE GREEN, NOT BEFORE IT, AND THAT IS VISIBLE.
       * The entry list, the race-week reveal and the starting grid are drawn by
       * `RaceSetup.tsx`, which builds its OWN field from the same seed and does
       * not read the pool — so those screens show the built-in names and the
       * race shows the pool names. Same seed, same driver ids, same cards; only
       * the labels differ, and the deal is deterministic, so this is the exact
       * field a fully wired race-week screen would have shown from the start.
       * It is one line in that file (`buildField` there, then these same three)
       * and that file was read-only for this change. The setup screen warns
       * about it in advance rather than letting a player meet it at the green.
       */
      const { names, shortfall, detail: dealDetail } = namesForField(
        poolRef.current?.pool ?? null,
        next.seed,
        field.drivers.length,
      );
      const { drivers, renamed, keptBuiltIn } = applyNamePool(field.drivers, names);
      // Held, not set yet: a race that throws below must not leave a report of
      // how a field that never took the green was named.
      const named: Naming = {
        renamed,
        keptBuiltIn,
        shortfall,
        poolDetail: poolRef.current?.detail ?? 'The name pool had not been looked for when this field was built.',
        dealDetail,
      };

      // The cards the RACE runs are the week's grid, not this field — race week
      // dealt them and they carry its results. Renaming is therefore a lookup
      // by driver id onto those cards, and a card whose name did not change is
      // passed through by reference so nothing downstream sees a new object for
      // no reason.
      const poolNameOf = new Map(drivers.map((d) => [d.card.driverId, d.card.name]));
      const grid = week.grid.map((card) => {
        const name = poolNameOf.get(card.driverId);
        return name && name !== card.name ? { ...card, name } : card;
      });
      const raced: RaceWeekResult = { ...week, grid };

      const track = trackCard(next.trackId);
      const setup: EngineRaceSetup = {
        track,
        drivers: raced.grid,
        seed: next.seed,
        totalTurns: next.totalTurns,
        // The EVENT's name when the race week screen had one — the calendar
        // names a scheduled race, and fourteen cards print two different races
        // (N-005), so the card's headline name is the wrong title for the
        // second of them. Falls back to the card for an exhibition.
        raceName: next.raceName ?? track.raceName ?? track.name,
        tvList: raced.tvList,
        feuds: raced.feuds,
        fuming: raced.fuming,
        startingChips: raced.chips,
      };
      /**
       * D-018 — EVERYTHING PAST THE GREEN, AS A CLOSURE.
       *
       * The only reason this tail became a function is manual dice. A seeded
       * race resolves in a call and this runs on the next line, exactly as
       * before. A hand-rolled race cannot: the engine has to stop nine hundred
       * times and wait for Howard's dice, so the result arrives from
       * `DiceTable` a long time later, and everything that has to happen when a
       * race exists has to be reachable from there too. One copy, both paths —
       * a second version of the post-race bookkeeping is exactly how the two
       * modes would start to disagree about what a finished race means.
       */
      const complete = (result: RaceResult) => {
        // New race, same season: forget which photos this race used, keep the
        // per-season counts that `maxPerSeason` is measured against.
        artHistory.current.startRace();
        // `raced`, not `week`: the payday and the finance fold look a driver's
        // card up out of the week that was run, and handing them the pre-rename
        // week would put two different names on one driver inside one screen.
        setRace({
          config: next,
          result,
          start: buildStartingGrid(setup),
          teams: field.teams,
          week: raced,
          // An exhibition has no season books. The standing-start purse below is
          // the honest answer for one race that belongs to no championship.
          seasonFinance: null,
        });
        setNaming(named);

        /**
         * A RACE IS UNSAVED WORK IN THIS BROWSER, AND THE NUDGE HAS TO KNOW.
         *
         * ⚠ NOT AN AUTOSAVE, and this is the line where one would go. Howard,
         * 17 Aug 2026: *"Save and load manually is the key to keeping progress
         * straight without burning up the I/O at supabase on auto saves."* What
         * is written here is a timestamp and a noun phrase in this browser's own
         * storage — no season, no roster, no ledger, and nothing that crosses the
         * wire. It exists so that a player who plays first and loads second is
         * told what the load will cost, instead of finding out afterwards.
         */
        const what = 'a race you set up and ran in this browser';
        const touchedAt = new Date().toISOString();
        markLocalProgress(what, touchedAt);
        // Mirrored into state as well as storage: the nudge reads state, and a
        // player who runs a race with the banner still on screen should see it
        // change from "load this" to "loading would discard what you just did"
        // without a reload.
        setLocal({ state: 'progress', what, lastTouchedAt: touchedAt });

        setConfig(next);
        setCursor(0);
        setPlaying(false);
        setSelected(null);
        setLit([]);
        setDossier(null);
        setError(null);
        setView('race');
      };

      // D-018 — "roll my own dice". Nothing is run here; the dice table owns
      // the race from the green to the flag and hands the finished result to
      // the same `complete` a seeded race uses.
      // D-EZ-103 — the exhibition's realism house rules (all on unless switched
      // off under Advanced), for the seeded race and the hand-rolled one alike.
      const options: RaceOptions = { ...(next.houseRules ?? {}) };
      if (next.handRolled) {
        setHandDice({ setup, options, complete });
        setHandProgress(null);
        setError(null);
        setView('dice');
        return;
      }
      complete(runRace(setup, options));
    } catch (e) {
      setError(friendlyError(e, 'starting the race'));
      setView('setup');
    }
  }, []);

  // ---- the career: thirty-six races, one at a time ------------------------

  /**
   * "THERE IS UNSAVED WORK IN THIS BROWSER" — the marker the cloud nudge reads.
   *
   * ⚠ NOT AN AUTOSAVE. Howard: *"Save and load manually is the key to keeping
   * progress straight without burning up the I/O at supabase on auto saves."*
   * What this writes is a timestamp and a noun phrase in local storage. No
   * season, no roster, no ledger, nothing over the wire.
   */
  const noteLocalWork = useCallback((what: string) => {
    const touchedAt = new Date().toISOString();
    markLocalProgress(what, touchedAt);
    setLocal({ state: 'progress', what, lastTouchedAt: touchedAt });
  }, []);

  /**
   * A SAVE LANDED — tell the nudge, so it stops warning about it.
   *
   * ⚠ THIS WAS MISSING AND IT SHOWED. Howard, 17 Aug 2026, forty seconds after
   * a successful save, mid-race-1: *"and what does this mean? Im literally in
   * the middle of my first race..."* — under LOADING WILL DISCARD NEWER-LOOKING
   * WORK, a banner whose body told him his season "was never saved anywhere".
   *
   * The nudge was never wrong about its inputs; it was never given this one.
   * The slot's server timestamp is naturally later than the browser's
   * last-played marker, so with no record of the save in between, the age
   * comparison read the gap as two copies drifting apart.
   *
   * Both facts are recorded here: the local marker becomes "this IS the save",
   * and the cloud state is refreshed from what the server actually stamped
   * rather than from a clock in this browser.
   */
  const noteSaved = useCallback((summary: SlotSummary) => {
    // D-EZ-103 — an account save protects what is on the workbench.
    setProtectedKey(workbenchKeyRef.current);
    const what = `${summary.leagueName} — season ${summary.seasonNumber}`;
    markLocalSaved(what, summary.savedAt || new Date().toISOString());
    setLocal({ state: 'saved', what, savedAt: summary.savedAt || null });
    setCloud({
      state: 'save',
      save: {
        kind: summary.racesRun === null ? 'career' : 'season',
        leagueName: summary.leagueName,
        seasonNumber: summary.seasonNumber,
        racesRun: summary.racesRun,
        savedAt: summary.savedAt,
      },
    });
  }, []);

  /**
   * START A SEASON. Nothing is raced here — `beginSeason` sets the year up and
   * runs nothing, which is what makes race 1 a thing the player presses a button
   * for rather than something that has already happened by the time they look.
   *
   * THE NAME POOL IS APPLIED HERE, ONCE, BEFORE THE ROSTER IS DEALT. An
   * exhibition renames its field at the green because the setup screen builds its
   * own; a season cannot do that — the roster IS the season, and renaming cards
   * after race 1 would put two names on one driver inside one championship. A
   * roster CARRIED out of a previous winter keeps the names it already has.
   */
  const beginCareer = useCallback(
    (
      startWith: SeasonStart,
      carried?: Carried,
      /**
       * D-EZ-103 — the league's settings and, for a NEW league, its name. A
       * carried season uses the settings chosen for the next season.
       */
      league?: { settings?: LeagueSettings; leagueName?: string },
    ): CareerLine | null => {
      try {
        const settings = normaliseSettings(league?.settings ?? nextSettings);
        let roster: DriverCard[];
        let teams: Team[];
        /*
          D-EZ-76 — a career that starts in a year that happened.

          Built once, here, and then never again. The printed `schedule` goes
          into `beginSeason` and everything from race 1 on is the ordinary
          engine — including the five alternate cards, which need no wiring at
          all: each one rides on its own driver's card and travels with him
          through transfers, saves and every winter after this one.

          The name pool is deliberately NOT applied — renaming Denny Hamlin
          would be the one thing that makes a 2025 season not a 2025 season —
          which is also why `setNaming(null)`.
        */
        const preset = !carried && startWith.scenario ? scenario2025(startWith.seed) : null;
        /*
          D-EZ-99 (a) — A NEW LEAGUE IS A NEW SHELF. Without a carry this is not
          season N+1 of anything: the previous league's seasons are put away
          (they stay on this device and in the account save if they were saved)
          rather than folded into this one's career totals.
        */
        const thisLeague = carried ? leagueId : newLeagueId();
        if (!carried) {
          putShelf(freshShelf(thisLeague));
          setLoaded(null);
          setBookSeason(null);
        }
        if (carried) {
          roster = carried.roster;
          teams = carried.teams;
          setNaming(null);
        } else if (preset) {
          roster = preset.roster;
          teams = preset.teams;
          setNaming(null);
        } else {
          const field = buildField(startWith.seed, { size: startWith.rosterSize });
          const { names, shortfall, detail: dealDetail } = namesForField(
            poolRef.current?.pool ?? null,
            startWith.seed,
            field.drivers.length,
          );
          const named = applyNamePool(field.drivers, names);
          roster = named.drivers.map((d) => d.card);
          teams = field.teams as Team[];
          setNaming({
            renamed: named.renamed,
            keptBuiltIn: named.keptBuiltIn,
            shortfall,
            poolDetail: poolRef.current?.detail ?? 'The name pool had not been looked for yet.',
            dealDetail,
          });
        }

        const state = beginSeason(
          {
            seed: startWith.seed,
            roster,
            teams: teams.map((t) => ({ teamId: t.teamId, name: t.name, driverIds: t.driverIds })),
            ...(preset ? { schedule: preset.schedule } : {}),
            // D-EZ-103 — the league settings: realism house rules (D-EZ-83…87)
            // through `race`, the sheet switches (D-EZ-98) through `houseRules`.
            options: seasonOptionsFor(settings, startWith.fieldSize),
            // D-EZ-91/94/97 — what the winter handed on; for the 2025 scenario,
            // the real drivers' priors and nothing else.
            ...(carried?.carry
              ? { inherited: carried.carry }
              : preset
                ? { inherited: emptyCareerCarry(preset.priors) }
                : {}),
          },
          {
            /**
             * THE BOOKS, RACE BY RACE. Off in `runSeason`, on here, because a
             * season being played one Sunday at a time wants to know what the
             * weekend paid before the next one. The year-end fold
             * (`settleSeason`) still prices the whole year off the finished
             * report, exactly as the Career tab does — this track is the live
             * view of the weekends, not a second set of books.
             */
            finance: { ownerOf: ownerMap(teams) },
          },
        );

        const line: CareerLine = {
          leagueId: thisLeague,
          seasonNumber: carried?.seasonNumber ?? 1,
          roster,
          teams,
          seasonsRun: carried?.seasonsRun,
          state,
        };
        setSeason(line);
        setNextSettings(settings);
        if (league?.leagueName) setLeagueName(league.leagueName);
        setHome('door');
        setConfig((c) => ({
          ...c,
          seed: startWith.seed,
          fieldSize: startWith.fieldSize,
          rosterSize: roster.length,
        }));
        setRace(null);
        setSeasonDice(null);
        setRolledWeekend(null);
        setHandDice(null);
        setError(null);
        setView('setup');
        noteLocalWork(`season ${carried?.seasonNumber ?? 1} of a career started in this browser`);
        return line;
      } catch (e) {
        setError(friendlyError(e, 'starting the season'));
        return null;
      }
    },
    [noteLocalWork, leagueId, putShelf, nextSettings],
  );

  /** D-EZ-103 — league creation's one button: a new league, season 1, the season home. */
  const createLeague = useCallback(
    (start: LeagueStart) => {
      beginCareer(
        { seed: start.seed, rosterSize: start.rosterSize, fieldSize: start.fieldSize, scenario: start.scenario },
        undefined,
        { settings: start.settings, leagueName: start.leagueName },
      );
    },
    [beginCareer],
  );

  /**
   * THE GREEN FLAG on a season race that has already been resolved.
   *
   * This is the only place the season state advances, and it advances by exactly
   * one race. Everything on the tape is read off the step: the race, the week
   * that set its grid, the money the weekend paid, and the standing start
   * rebuilt by `seasonStartingGrid` from the state as it was BEFORE the race.
   */
  const takeGreen = useCallback(
    (step: SeasonRaceStep) => {
      if (!season) return;
      try {
        // New race, same season: forget which photos this race used, keep the
        // per-season counts `maxPerSeason` is measured against.
        artHistory.current.startRace();
        const config: RaceConfig = {
          trackId: step.race.track.id,
          totalTurns: step.race.scheduledTurns,
          seed: step.result.raceSeed,
          fieldSize: season.state.options.fieldSize ?? MAX_STARTING_FIELD,
          rosterSize: season.roster.length,
          raceName: step.result.scheduled.raceName,
          seasonRaceNumber: step.result.raceNumber,
        };
        setRace({
          config,
          result: step.race,
          start: seasonStartingGrid(season.state, step),
          teams: season.teams,
          week: step.week,
          seasonFinance: step.finance,
        });
        setSeason({ ...season, state: step.state });
        setSeasonDice(null);
        setRolledWeekend(null);
        setHandDice(null);
        setCursor(0);
        setPlaying(false);
        setSelected(null);
        setLit([]);
        setDossier(null);
        setError(null);
        setView('race');
        noteLocalWork(
          `race ${step.result.raceNumber} of ${SEASON_RACES} of a career in this browser`,
        );
      } catch (e) {
        setError(friendlyError(e, 'getting the next race ready'));
      }
    },
    [season, noteLocalWork],
  );

  /** D-018 — hand-roll the next season race. The dice table owns it from here. */
  const handRollSeasonRace = useCallback(() => {
    if (!season || isSeasonComplete(season.state)) return;
    setSeasonDice({ state: season.state, raceNumber: season.state.nextRace });
    setHandProgress(null);
    setHandDice(null);
    setError(null);
    setView('dice');
  }, [season]);

  /**
   * RACE 36 IS OVER: the report, the money, the winter.
   *
   * Deferred one macrotask so the button can repaint as "Settling…" before a
   * second of synchronous arithmetic — a full season fold plus `runOffseason` —
   * takes the tab. The season is then put down and the career takes its place,
   * which is the thing Save and the record book read.
   */
  const settleLine = useCallback(
    (line: CareerLine) => {
      if (!isSeasonComplete(line.state)) return;
      setSettling(true);
      setError(null);
      window.setTimeout(() => {
        try {
          const report = finishSeason(line.state);
          // D-EZ-103 — the rules it was raced under go on the settled season.
          const rules = settingsOfSeasonOptions(line.state.options);
          const career = settleSeason({
            seed: line.state.seed,
            seasonNumber: line.seasonNumber,
            roster: line.roster,
            teams: line.teams,
            season: report,
            seasonsRun: line.seasonsRun,
            raceRules: rules.race,
            houseRules: rules.sheet,
          });
          // D-EZ-99 — folded onto its OWN league's shelf, never another's.
          putShelf(
            foldSettled(
              { leagueId, career: reachableCareer, history: reachableHistory },
              career,
              line.leagueId,
            ),
          );
          setSeason(null);
          setView('career');
        } catch (e) {
          setError(friendlyError(e, 'settling the season'));
        } finally {
          setSettling(false);
        }
      }, 0);
    },
    [reachableCareer, reachableHistory, leagueId, putShelf],
  );

  const settle = useCallback(() => {
    if (!season || !isSeasonComplete(season.state) || settling) return;
    settleLine(season);
  }, [season, settling, settleLine]);

  /**
   * ⚠ FAST-FORWARDING A SEASON THAT IS ALREADY UNDER WAY. D-EZ-59.
   *
   * Howard, 18 Aug 2026: *"if I'm in the middle of a season, how do I sim the
   * rest of it to the playoffs beginning and then an option to finsh the season
   * to the season rollover into the winter progressions?"*
   *
   * There was no answer, and that was the gap: a season could be started race by
   * race and then only ever finished race by race, thirty-six times. This runs
   * the SAME `runSeasonRace` the Set up tab runs, in a loop, with no shortcut
   * and no second procedure — so a season simmed to the playoffs is byte-for-byte
   * the season that would have been played to the playoffs.
   *
   * ⚠ TWO RACES PER TICK, NOT THIRTY-SIX IN ONE. A whole season is a second or
   * two of synchronous arithmetic; done in one go the tab freezes and the
   * progress bar never paints, which reads as a hang. Yielding between pairs
   * costs nothing and lets the count move.
   */
  const runAhead = useCallback(
    (from: CareerLine, target: 'playoffs' | 'end') => {
      if (fastForward || settling || isSeasonComplete(from.state)) return;
      // The first PLAYOFF race is the one after the regular season ends.
      const stopBefore = target === 'playoffs' ? REGULAR_SEASON_RACES + 1 : SEASON_RACES + 1;
      const total = stopBefore - from.state.nextRace;
      if (total <= 0) return;
      const label = target === 'playoffs' ? 'to the playoffs' : 'to the checkered flag';
      setError(null);
      setFastForward({ done: 0, total, label });

      let state = from.state;
      const tick = () => {
        window.setTimeout(() => {
          try {
            for (let i = 0; i < 2 && state.nextRace < stopBefore && !isSeasonComplete(state); i++) {
              state = runSeasonRace(state).state;
            }
            setFastForward({ done: total - (stopBefore - state.nextRace), total, label });
            if (state.nextRace < stopBefore && !isSeasonComplete(state)) {
              tick();
              return;
            }
            const line = { ...from, state };
            setSeason(line);
            // The tape belongs to a race that is now several Sundays ago.
            setRace(null);
            setSeasonDice(null);
            setRolledWeekend(null);
            setHandDice(null);
            setFastForward(null);
            noteLocalWork(
              `season ${line.seasonNumber} simulated ${label} in this browser`,
            );
            if (target === 'end') settleLine(line);
          } catch (e) {
            setError(friendlyError(e, 'running the rest of the season'));
            setFastForward(null);
          }
        }, 0);
      };
      tick();
    },
    [fastForward, settling, settleLine, noteLocalWork],
  );

  /** D-EZ-103 — "Simulate the rest of the season" / "to the playoffs", from the season home. */
  const simulateAhead = useCallback(
    (target: 'playoffs' | 'end') => {
      if (season) runAhead(season, target);
    },
    [season, runAhead],
  );


  /** Next season, out of this one's winter — playable race by race. */
  const continueCareer = useCallback(
    (from: CareerSeason, simulate = false) => {
      const line = beginCareer(
        {
          // The same derivation the Career tab's "next season" uses, so one
          // league's seasons follow the same sequence whichever way they are run.
          seed: from.seed + (from.seasonNumber + 1) * 7919,
          rosterSize: from.offseason.roster.length,
          fieldSize: MAX_STARTING_FIELD,
        },
        {
          roster: from.offseason.roster,
          teams: from.offseason.teams,
          seasonNumber: from.seasonNumber + 1,
          seasonsRun: from.offseason.seasonsRun,
          carry: from.offseason.carry,
        },
      );
      // D-EZ-103 — "Simulate all of season N+1": the same season, run whole.
      if (simulate && line) runAhead(line, 'end');
    },
    [beginCareer, runAhead],
  );

  // ---- the tape ----------------------------------------------------------
  const turnsRun = race?.result.turnsRun ?? 0;
  const timer = useRef<number | null>(null);

  // Load the art library once. It is a few hundred rows of metadata and every
  // moment in every race selects from it, so it is held in memory rather than
  // queried per event. A failure here is deliberately silent: the moment panel
  // simply never appears, and no picture is strictly better than a broken race.
  useEffect(() => {
    let live = true;
    loadLibrary()
      .then((lib) => { if (live) setArtLibrary(lib); })
      .catch(() => { /* no library, no panel */ });
    return () => { live = false; };
  }, []);

  /**
   * BOOT. Who is signed in, what is in their slot, what this browser holds, and
   * whether there is a pool of AI names — asked once, after first paint.
   *
   * NOTHING HERE BLOCKS THE GAME. The effect runs after the first render, so
   * the race-week screen is on screen and usable while the questions are still
   * in flight; `ensureNamePool` waits up to twenty seconds for a batch and the
   * player can be halfway through setting up a race by the time it answers. The
   * one thing that depends on the answer — naming a field — happens at the
   * green, which is the latest possible moment and the only one that matters.
   */
  useEffect(() => {
    let live = true;
    void boot().then((b) => {
      if (!live) return;
      setAccount(b.account);
      setCloud(b.cloud);
      setLocal(b.local);
      setSeenHereBefore(b.seenHereBefore);
      setPool(b.pool);
      poolRef.current = b.pool;
    });
    return () => {
      live = false;
    };
  }, []);

  /**
   * Signing in or out changes the answer to "is there a save", so the slot is
   * re-read when the account changes — including changes this tab did not
   * cause, such as another tab signing out or a refresh token expiring.
   *
   * THE NAME POOL IS NOT RE-ASKED. Requirement 2 is that the model writes one
   * batch and re-seeding reshuffles it; a fresh sign-in is not a reason to
   * write more names, and a signed-in player is exactly the player most likely
   * to have a league whose vocabulary must not move underneath them.
   */
  useEffect(() => {
    const off = onAccountChange((next, event) => {
      // Set FIRST, and for every event including INITIAL_SESSION: this is the
      // value the Art tab is gated on, and the early return below is about not
      // making a second network call, not about ignoring who is signed in.
      setAccount(next);
      setSeenHereBefore(seenBeforeThisVisit(next));
      // Supabase fires INITIAL_SESSION on subscribe, for the session the boot
      // sequence is already reading. Peeking again would be a second request
      // for an answer that is already on its way — and this project counts its
      // Supabase I/O on purpose.
      if (event === 'INITIAL_SESSION') return;
      void readCloud(next).then(setCloud);
    });
    return off;
  }, []);

  /**
   * THE ART LIBRARY IS THE OWNER'S TOOL, AND ONLY HIS.
   *
   * `isOwner` compares the signed-in Supabase session's email against the one
   * constant in `lib/auth.ts`; signed out is never the owner.
   *
   * This line hides a TAB and nothing more — a hidden tab has never stopped a
   * request, and it should not be read as protection. What actually protects the
   * library is server-side: `worker/owner.ts` verifies the caller's token with
   * Supabase and refuses every art write that is not the owner's, whether it
   * came from this screen, from another browser, or from curl.
   */
  const ownerHere = isOwner(account);

  /**
   * Somebody who is ON the art screen when the account changes — signing out,
   * a token expiring, another tab switching user — must not be left standing on
   * a screen that no longer has a way back to it. They land on Set up, which
   * is where the app opens.
   */
  useEffect(() => {
    // Both owner-only screens, moved off together. A non-owner sitting on the
    // marks cabinet is the same problem as one sitting on the art library.
    if (!ownerHere && (view === 'art' || view === 'logos')) setView('setup');
  }, [ownerHere, view]);

  /**
   * SHOULD THE PLAYER BE TOLD TO LOAD BEFORE THEY PLAY?
   *
   * The whole decision, including every word of it, is `decideCloudNudge` —
   * pure, and walked across its entire input matrix in `cloudNudge.test.ts`.
   * Nothing is decided here. While `cloud` is null the shell has not looked
   * yet, and it says nothing at all: a warning invented before the question was
   * asked is exactly the kind of guess this project does not ship.
   */
  const nudge = useMemo(
    () => (cloud === null ? null : decideCloudNudge({ cloud, local, seenHereBefore })),
    [cloud, local, seenHereBefore],
  );

  /**
   * The nudge's one action: put the account bar where the player is looking.
   *
   * ⚠ IT SCROLLED AND NOTHING ELSE, AND THAT WAS NOT ENOUGH. Howard, 17 Aug
   * 2026: *"clicking take me to the save slot just scrolls the screen down an
   * inch"*. He was right, and the reason is that the bar is usually ALREADY on
   * screen — it is a strip at the top of the shell — so `scrollIntoView` had
   * almost nothing to do and the two-second highlight was easy to miss.
   *
   * So the cursor moves to the Save button itself. Unmissable, correct for a
   * keyboard user, and announced by a screen reader — none of which a scroll
   * position does. The focus is queued behind the smooth scroll so the browser
   * does not fight its own animation.
   *
   * It still does not SAVE. Saving is the bar's job and there is one Save
   * button in the application; a second path to it here is the thing
   * `CloudNudge`'s own note refuses.
   */
  const showAccount = useCallback(() => {
    setShellPanel('account');
    accountRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    setFlagAccount(true);
    window.setTimeout(() => setFlagAccount(false), 2000);
    window.setTimeout(() => {
      const save = document.getElementById(SAVE_BUTTON_ID);
      // A disabled button cannot take focus, and there is nothing useful to
      // move the cursor to in that case — the sentence under it already says
      // why. Better to leave focus where it was than to steal it and land
      // nowhere.
      if (save instanceof HTMLButtonElement && !save.disabled) save.focus();
    }, 320);
  }, []);

  /**
   * A league came back out of the slot. TWO KINDS COME BACK, and they land in
   * two different places.
   *
   * A SEASON IN PROGRESS goes STRAIGHT ONTO THE WORKBENCH. `league.season` is a
   * `LiveSeason` — the same shape `CareerLine` is — carrying a `SeasonState` that
   * `runSeasonRace` will accept, so the player is put back at the exact next race
   * and the Set up tab takes over. Nothing is re-derived and nothing is
   * guessed: this is the season that was saved, reassembled from the head and its
   * race rows (`lib/seasonSlot.ts`), with the ledger checked for continuity
   * before it is handed over.
   *
   * A FINISHED SEASON is what it always was. `league.carry` is
   * `{ roster, teams, seasonsRun }` — the post-winter state, and exactly
   * `nextSeasonOptions()`'s inputs — so the player can CONTINUE, which means
   * season N+1 off the carried roster and garage, played race by race
   * (`continueLoaded`). The button is offered beside the load, not implied.
   *
   * WHAT A FINISHED SEASON STILL CANNOT DO, and the shell says so in one line:
   * come back as season N itself on the workbench. `restored` carries the roster,
   * the race list and the two ledgers, but not the season fold, the finance result
   * or the winter that a `CareerSeason` is made of, and assembling those out of
   * what is missing would be inventing a season that was never run.
   *
   * THE LOCAL MARKER IS DELIBERATELY NOT CLEARED for a finished season: clearing
   * it would mean "the work in this browser has been replaced", and adopting a
   * carry replaces nothing. Loading a season in progress DOES replace the
   * workbench, so that branch marks the new state as this browser's current work
   * instead — the nudge would otherwise keep warning about a local season that is
   * no longer there.
   */
  const onLeagueLoaded = useCallback(
    (league: LoadedLeague) => {
      setLeagueName(league.leagueName);
      // D-EZ-103 — the league's settings come back with it: the head's (the
      // next season's), else the season's own, else the last settled season's.
      setNextSettings(
        league.settings ??
          (league.kind === 'season'
            ? settingsOfSeasonOptions(league.season.state.options)
            : settingsOfCareer(league.archive.at(-1))),
      );
      setHome('door');
      /*
        D-EZ-65 — THE SEASONS COME BACK WITH IT.

        Howard, 19 Aug 2026, on loading a finished season and finding the record
        book empty: *"I don't see how I access old seasons from this page."* The
        answer was that the slot never carried them — `snapshot` is a relational
        export for a database and cannot be read back as a season. Now the file
        carries the seasons themselves with the race tapes stripped, so loading a
        league puts its whole history on the workbench: the record book, the
        winter, the garage and every career total.

        Empty on a file written before v3, and nothing is invented to fill it.
      */
      /*
        D-EZ-66 — AND A SEASON SLOT CARRIES THEM TOO.

        This was `league.kind === 'career'` only, which meant a league saved
        mid-season — the ordinary way a league is saved — came back with an empty
        record book even after D-EZ-65. Both kinds now carry the shelf, so the
        branch is on whether there IS one and not on which kind of file it is.
      */
      /*
        D-EZ-99 (a) — THE SHELF IS REPLACED, ALWAYS. It used to be replaced only
        when the file had seasons in it, so loading a league with none left the
        previous league's seasons on the shelf to be folded into this one.
      */
      putShelf(shelfFromArchive(league.leagueId, league.archive));
      setBookSeason(null);
      setCloud({
        state: 'save',
        save: {
          leagueName: league.leagueName,
          seasonNumber: league.seasonNumber,
          kind: league.kind,
          racesRun: league.kind === 'season' ? league.racesRun : null,
          savedAt: league.savedAt,
        },
      });
      /**
       * THE BROWSER NOW HOLDS THE SLOT'S SEASON — say so, rather than dismissing.
       *
       * `clearLocalProgress` has always documented itself as "called after a
       * load succeeds" and NOTHING HAS EVER CALLED IT. So a browser kept a
       * warning about work that a load had already replaced, forever.
       *
       * `markLocalSaved` and not `clearLocalProgress`: the browser does not hold
       * *nothing*, it holds exactly what is in the slot — which is a fact worth
       * recording, and the one the nudge's new first branch reads.
       */
      const what = `${league.leagueName} — season ${league.seasonNumber}`;
      markLocalSaved(what, league.savedAt || new Date().toISOString());
      setLocal({ state: 'saved', what, savedAt: league.savedAt || null });

      // They did the thing the nudge asked for. Asking again in the same breath
      // is how a warning becomes wallpaper. Dismissal is by KIND, so if the
      // situation changes into a different one the nudge comes back.
      setDismissedNudge(nudge?.kind ?? null);

      if (league.kind === 'season') {
        /**
         * The loaded season IS the season now. `loaded` is cleared because the
         * "continue into season N+1" panel it drives is about a finished season
         * and would be an invitation to abandon the one just picked up.
         *
         * ⚠ IF THIS SHELL EVER PUTS A FUNCTION IN `SeasonOptions` — a
         * `raceWeek.tvQualityFor` or a `race.onTurn` — IT MUST BE RE-ATTACHED
         * HERE with `withSeasonOptions`. Functions do not survive a save file, so
         * a loaded season would carry the option with the behaviour missing and
         * every race after the load would quietly run a different rule. Today
         * `beginCareer` passes `{ fieldSize }` and nothing else, so there is
         * nothing to put back and the state is complete as it stands.
         */
        setLoaded(null);
        setSeason(league.season);
        setConfig((c) => ({
          ...c,
          seed: league.season.state.seed,
          fieldSize: league.season.state.options.fieldSize ?? MAX_STARTING_FIELD,
          rosterSize: league.season.roster.length,
        }));
        setRace(null);
        setSeasonDice(null);
        setRolledWeekend(null);
        setHandDice(null);
        setError(null);
        setView('setup');
        noteLocalWork(
          `season ${league.seasonNumber}, ${league.racesRun} of ${SEASON_RACES} races, loaded into this browser`,
        );
        return;
      }

      // A finished league replaces the workbench too: a season from another
      // league must not stay live beside it (D-EZ-99). It is still on this device.
      setSeason(null);
      setLoaded(league);
      // D-EZ-103 — Home shows the next-season card for it.
      setView('setup');
    },
    [nudge, noteLocalWork, putShelf],
  );

  /*
    D-EZ-103 — CONTINUE WHERE YOU LEFT OFF. A league is copied to this device
    after every race (lib/localBackup.ts). On the front door the newest one is
    offered as "Continue", so a reload never strands a player at "Start a new
    league" with their season sitting in IndexedDB.
  */
  useEffect(() => {
    let live = true;
    void listBackups(backupOwner(account?.id ?? null))
      .then((list) => {
        if (!live) return;
        const newest = [...list].sort((a, b) => b.savedAt.localeCompare(a.savedAt))[0] ?? null;
        setDeviceLeague(newest);
      })
      .catch(() => live && setDeviceLeague(null));
    return () => {
      live = false;
    };
  }, [account, backupTick]);

  const resumeDeviceLeague = useCallback(async () => {
    if (!deviceLeague) return;
    setResumeError(null);
    const r = await restoreBackup(backupOwner(account?.id ?? null), deviceLeague.leagueId);
    if ('ok' in r) {
      setResumeError(r.message);
      return;
    }
    onLeagueLoaded(r);
  }, [deviceLeague, account, onLeagueLoaded]);

  /**
   * D-EZ-99 (c) — THE COPY ON THIS DEVICE, after every race and every settle.
   *
   * Not the account save (that stays a button — Howard's Supabase I/O rule): this
   * is IndexedDB on this browser, per account and per league, and it writes only
   * what it does not already hold — one race after a race, one season after a
   * winter. Debounced so a burst of state changes is one write.
   */
  useEffect(() => {
    if (!season && !reachableCareer) return;
    const key = progressKey({ leagueId, season, career: reachableCareer, history: reachableHistory });
    const handle = window.setTimeout(() => {
      void backupLeague(backupOwner(account?.id ?? null), {
        leagueId,
        leagueName,
        season,
        career: reachableCareer,
        history: reachableHistory,
        settings: nextSettings,
      }).then((r) => {
        if (r.ok) {
          setBackupTick((n) => n + 1);
          setProtectedKey(key);
        }
      });
    }, 700);
    return () => window.clearTimeout(handle);
  }, [season, reachableCareer, reachableHistory, leagueId, leagueName, account, nextSettings]);

  /**
   * D-EZ-103 — THE LEAVE-PAGE WARNING, only when leaving loses something: a race
   * being rolled by hand (its dice are on this page only), or a league whose
   * latest race has not reached the device copy or the account yet.
   */
  const workbenchKey = progressKey({ leagueId, season, career: reachableCareer, history: reachableHistory });
  workbenchKeyRef.current = workbenchKey;
  const leaveWarning = unsavedWarning({
    handRolling: Boolean(handDice || seasonDice),
    handFaces: handProgress?.faces ?? 0,
    progress: workbenchKey,
    protectedKey,
  });
  useEffect(() => {
    if (!leaveWarning) return;
    const onLeave = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Most browsers show their own words; the ones that still read this get ours.
      e.returnValue = leaveWarning;
      return leaveWarning;
    };
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [leaveWarning]);

  /**
   * ADOPT A LOADED FINISHED SEASON: start its next season, playable race by race.
   *
   * The seed moves the same way it moves between two seasons played in one
   * sitting, so a league reloaded from the cloud continues on the calendar it
   * would have had if the browser had never been closed.
   */
  const continueLoaded = useCallback(
    (league: LoadedCareer, simulate = false) => {
      const next = league.seasonNumber + 1;
      const line = beginCareer(
        {
          seed: league.seed + next * 7919,
          rosterSize: league.carry.roster.length,
          fieldSize: MAX_STARTING_FIELD,
        },
        {
          roster: league.carry.roster,
          teams: league.carry.teams,
          seasonNumber: next,
          seasonsRun: league.carry.seasonsRun,
          // D-EZ-91/94/97 — sponsors, banks, feuds, injuries and priors.
          ...(league.carry.career ? { carry: league.carry.career } : {}),
        },
      );
      if (line) setLoaded(null);
      if (simulate && line) runAhead(line, 'end');
    },
    [beginCareer, runAhead],
  );

  /*
    D-EZ-102 — WHEN THE FLAG FALLS, GO TO VICTORY LANE. The moment the cursor
    reaches the finish (not on opening a race that is already over), the
    winner's moment is brought into view after the last cars settle.
  */
  const wasAtFinish = useRef<boolean | null>(null);
  useEffect(() => {
    wasAtFinish.current = null;
  }, [race]);
  useEffect(() => {
    const atFinish = Boolean(race) && cursor >= turnsRun && turnsRun > 0;
    const before = wasAtFinish.current;
    wasAtFinish.current = atFinish;
    if (!atFinish || before !== false || view !== 'race') return;
    const t = window.setTimeout(() => {
      try {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        document.querySelector('.vl')?.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
      } catch {
        /* no layout (tests) */
      }
    }, 900);
    return () => window.clearTimeout(t);
  }, [race, cursor, turnsRun, view]);

  /*
    D-EZ-102 — THE LEAGUE BEHIND THE RACE ON THE TAPE. What the league knew
    going into it (the booth's memory: built off the season as it stood BEFORE
    this race). Also what rides on a commissioner throw (D-EZ-104).
  */
  const boothSeason = useMemo(() => {
    if (!race || !season || !race.config.seasonRaceNumber) return undefined;
    return (
      seasonContextBefore({ history: reachableHistory, career: reachableCareer, live: season }, race.config.seasonRaceNumber) ??
      undefined
    );
  }, [race, season, reachableHistory, reachableCareer]);

  /*
    D-EZ-104 / D-EZ-105 — STAKES BEFORE THE DICE. The race as the stakes cards
    read it (the board before each turn, the race's own rules) and, at "You roll
    the big ones", the commissioner's stops for the whole race. Every stop is
    decided from what the table had shown before that throw
    (presentation/stakes/stakes.ts); planning them all here only saves doing it
    again on every render.

    D-EZ-111 — the booth's league (cast, people layer and rivalry book CUT AT
    THE PREVIOUS RACE, last week, the analyst's pick) feeds the race radio and
    the stakes cards' `pairNote` / `driverNote` ("Ackerd wrecked him here last
    season", "Ballew has a short fuse").
  */
  const boothLeague = useMemo(() => {
    if (!race || !season || !race.config.seasonRaceNumber) return null;
    return boothLeagueFor({ history: reachableHistory, career: reachableCareer, live: season }, race.config.seasonRaceNumber, boothSeason ?? null);
  }, [race, season, reachableHistory, reachableCareer, boothSeason]);
  const stakesContext = useMemo<StakesContext | undefined>(
    () => (boothSeason ? { season: boothSeason, ...(stakesNotes(boothLeague) ?? {}) } : undefined),
    [boothSeason, boothLeague],
  );
  const stakesFrame = useMemo<RaceFrame | null>(() => {
    if (!race) return null;
    const options = race.config.seasonRaceNumber ? season?.state.options?.race : race.config.houseRules;
    return { result: race.result, start: race.start, rules: stakesRulesFrom(options as Parameters<typeof stakesRulesFrom>[0]) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [race]);
  const commissioner = useMemo(
    () => (stakesFrame && pace === 'commissioner' ? commissionerRace(stakesFrame, stakesContext) : null),
    [stakesFrame, stakesContext, pace],
  );
  /** D-EZ-105 — the results of the commissioner's throws this race, newest last, for the race radio. */
  const [calls, setCalls] = useState<{ key: string; turn: number; title: string; line: string }[]>([]);
  useEffect(() => setCalls([]), [race]);
  const onCall = useCallback((m: TableMoment, line: string) => {
    const turn = Number(m.key.split('-')[0]);
    setCalls((c) => (c.some((x) => x.key === m.key) ? c : [...c, { key: m.key, turn, title: m.card.title, line }]));
  }, []);
  /** The commissioner's throws before turn `t`, to number the next one. */
  const thrownBefore = useCallback(
    (t: number) => [...(commissioner?.plans.values() ?? [])].filter((p) => p.turn < t).reduce((n, p) => n + p.moments.length, 0),
    [commissioner],
  );

  /** D-EZ-102 — turns a running broadcast holds on (the Big One, rain, a red flag). */
  const momentHolds = useMemo(() => (race ? holdTurns(race.result) : new Set<number>()), [race]);
  /** D-EZ-102 — a moment card that has just appeared is given time to be read (never under reduced motion). Only the turn already shown is consulted. */
  const readHold = useCallback((turn: number) => {
    try {
      return momentHolds.has(turn) && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 2600 : 0;
    } catch {
      return 0;
    }
  }, [momentHolds]);

  /** D-EZ-103 — Big moments rolls a turn on the table without stopping the run. */
  const playTable = useCallback((target: number) => {
    if (!race || activeTable.current || target < 1 || target > turnsRun) return;
    if (!tablePreferences.animated) { setCursor(target); return; }
    const session = { target, replay: false, serial: ++tableSerial.current };
    activeTable.current = session;
    setTableSession(session);
  }, [race, turnsRun, tablePreferences.animated]);

  /**
   * D-EZ-105 — open the table for the commissioner: the turn's pivotal throws
   * wait for his Roll; the rest resolve themselves. Opens even with dice motion
   * off (the throw is still his; the faces simply land at once).
   */
  const playCommissioner = useCallback((target: number, moments: readonly TableMoment[]) => {
    if (!race || activeTable.current || target < 1 || target > turnsRun) return;
    const session: PendingTableTurn = {
      target,
      replay: false,
      serial: ++tableSerial.current,
      commissioner: { moments, thrownBefore: thrownBefore(target) },
    };
    activeTable.current = session;
    setTableSession(session);
  }, [race, turnsRun, thrownBefore]);

  useEffect(() => {
    setPeek(null);
    setMoment(null);
    lastBig.current = 0;
  }, [race]);

  useEffect(() => {
    if (!playing || !race || view !== 'race' || tableSession) return;
    if (cursor >= turnsRun) { setPlaying(false); return; }
    const clear = () => { if (timer.current !== null) window.clearTimeout(timer.current); };
    if (pace === 'commissioner') {
      /*
        D-EZ-105 — YOU ROLL THE BIG ONES. The same two gates as Big moments (what
        is on screen, then the revealed race cam call) open the table; inside an
        opened turn the planned stops wait for the commissioner's Roll. A turn
        with no stop goes by on its own.
      */
      const step = planCommissionerStep(
        visibleTurnState(raceViewAt(race.result, race.start, cursor), race.result.scheduledTurns),
        peek,
      );
      const hold = (cursor > 0 && lastBig.current === cursor ? 900 : 0) + readHold(cursor);
      const peeked = peek?.turn === step.target;
      const moments = step.kind === 'table' ? (commissioner?.plans.get(step.target)?.moments ?? []) : [];
      if (step.kind === 'peek') {
        timer.current = window.setTimeout(() => setPeek(revealCamCall(race.result.turns[step.target - 1])), hold + 40);
      } else if (step.kind === 'reveal' || moments.length === 0) {
        timer.current = window.setTimeout(() => setCursor(step.target), hold + (peeked ? 380 : msForPace('commissioner')));
      } else {
        timer.current = window.setTimeout(() => {
          lastBig.current = step.target;
          setMoment({ turn: step.target, reason: step.reason });
          playCommissioner(step.target, moments);
        }, hold + (peeked ? 380 : 120));
      }
      return clear;
    }
    if (pace !== 'moments') {
      timer.current = window.setTimeout(() => setCursor((c) => Math.min(turnsRun, c + 1)), msForPace(pace) + readHold(cursor));
      return clear;
    }
    /*
      BIG MOMENTS — one decision per tick, from what is ON SCREEN only: the board
      as of `cursor` and the race cam call already revealed (`peek`). See
      `presentation/bigMoments.ts` for why it can never stop for a wreck it has
      not shown. After a big moment the board holds so the cars can be seen move.
    */
    const step = planMomentStep(
      visibleTurnState(raceViewAt(race.result, race.start, cursor), race.result.scheduledTurns),
      peek,
    );
    const hold = (cursor > 0 && lastBig.current === cursor ? AFTER_BIG_HOLD_MS : 0) + readHold(cursor);
    const peeked = peek?.turn === step.target;
    if (step.kind === 'peek') {
      timer.current = window.setTimeout(() => setPeek(revealCamCall(race.result.turns[step.target - 1])), hold + 60);
    } else if (step.kind === 'reveal') {
      timer.current = window.setTimeout(() => setCursor(step.target), hold + (peeked ? PEEK_HOLD_MS : msForPace('moments')));
    } else {
      timer.current = window.setTimeout(() => {
        lastBig.current = step.target;
        setMoment({ turn: step.target, reason: step.reason });
        playTable(step.target);
      }, hold + (peeked ? PEEK_HOLD_MS : 160));
    }
    return clear;
  }, [playing, cursor, turnsRun, pace, race, view, tableSession, peek, playTable, playCommissioner, commissioner, readHold]);

  const beginTableTurn = useCallback((target: number, replay = false, force = false) => {
    if (!race || activeTable.current || target < 1 || target > turnsRun) return;
    setPlaying(false);
    setSelected(null);
    setLit([]);
    // D-EZ-105 — at "You roll the big ones" a stepped turn still stops for its
    // big throws and lets the rest resolve themselves.
    if (!replay && pace === 'commissioner' && commissioner) {
      const session: PendingTableTurn = {
        target,
        replay,
        serial: ++tableSerial.current,
        commissioner: { moments: commissioner.plans.get(target)?.moments ?? [], thrownBefore: thrownBefore(target) },
      };
      activeTable.current = session;
      setTableSession(session);
      return;
    }
    if (!replay && !force && !tablePreferences.animated) { setCursor(target); return; }
    const session = { target, replay, serial: ++tableSerial.current };
    activeTable.current = session;
    setTableSession(session);
  }, [race, turnsRun, tablePreferences.animated, pace, commissioner, thrownBefore]);

  const completeTableTurn = useCallback(() => {
    const session = activeTable.current;
    if (!session) return;
    activeTable.current = null;
    setTableSession(null);
    if (!session.replay) setCursor(session.target);
    // D-EZ-101 — on a phone the board sits above the dice: once the dice have
    // spoken, bring the board back into view so the cars are seen moving.
    try {
      if (!session.replay && window.matchMedia('(max-width: 980px)').matches) {
        document.querySelector('.racemain__stage')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    } catch {
      /* no layout to scroll (tests) */
    }
  }, []);

  const advance = useCallback(() => {
    beginTableTurn(cursor + 1);
  }, [cursor, beginTableTurn]);

  const toTheFlag = useCallback(() => {
    // "To the flag" is the deliberate skip (CONTINUATION.md): it also closes a
    // dice table that is mid-sequence. Nothing is resampled — the cursor simply
    // jumps to the retained finish.
    if (activeTable.current) {
      activeTable.current = null;
      setTableSession(null);
    }
    setPlaying(false);
    setSelected(null);
    setLit([]);
    setCursor(turnsRun);
  }, [turnsRun]);

  /** UI-001 — back to the green. The whole race, from a standing start. */
  const rewind = useCallback(() => {
    if (activeTable.current) {
      activeTable.current = null;
      setTableSession(null);
    }
    setPlaying(false);
    setSelected(null);
    setLit([]);
    setCursor(0);
  }, []);

  /**
   * UI-001 — ONE TURN BACK, which is what Howard actually asked for:
   *
   *   "a button that allows the user to reverse the card move visually so they
   *    can re-see it if they missed it."
   *
   * "Back to the green" was standing in for this and is not the same thing —
   * restarting a 50-turn race to re-watch turn 34 is not re-seeing a move, it is
   * losing your place. Stepping the cursor back one re-applies the previous
   * RaceState, and because every card is transform-positioned by driver id the
   * browser tweens the pass backwards on its own. No engine call, no re-run.
   *
   * DISPLAY ONLY. It cannot alter a result: the cursor is a view into turns that
   * have already been resolved, and the ledger is append-only by database
   * trigger. Going back and forward changes nothing but what is on screen.
   */
  const stepBack = useCallback(() => {
    if (activeTable.current) return;
    setPlaying(false);
    setSelected(null);
    setLit([]);
    setCursor((c) => Math.max(0, c - 1));
  }, []);

  /**
   * UI-001 — scrubbing the ledger. Clicking a past ruling snaps the boards to
   * the turn that produced it, so the tape and the boards can never be looking
   * at two different moments. Selecting a ruling from a turn already on screen
   * just lights its actors, as before.
   */
  const scrubTo = useCallback((turn: number) => {
    setPlaying(false);
    setCursor(turn);
  }, []);

  // Space runs and pauses; the right arrow steps a turn. Both stay out of the
  // way of anything focusable.
  useEffect(() => {
    if (view !== 'race' || !race) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && /^(INPUT|SELECT|TEXTAREA|BUTTON)$/.test(el.tagName)) return;
      if (activeTable.current) return;
      if (e.code === 'Space') {
        e.preventDefault();
        setPlaying((p) => (cursor >= turnsRun ? false : !p));
      } else if (e.code === 'ArrowRight') {
        e.preventDefault();
        setPlaying(false);
        advance();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [view, race, cursor, turnsRun, advance]);

  // ---- what the boards show ----------------------------------------------
  const shown = useMemo(() => {
    if (!race) return null;
    return raceViewAt(race.result, race.start, cursor);
  }, [race, cursor]);

  /**
   * The cars as they stood going INTO the turn on the dice table (7 Oct 2026),
   * so the driver cards under the dice show each man where he was when the
   * throw was made — on a replay as much as on the first roll.
   */
  const tableTarget = tableSession?.target;
  const tableField = useMemo(() => {
    if (!race) return undefined;
    const before = tableTarget !== undefined ? raceViewAt(race.result, race.start, Math.max(0, tableTarget - 1)) : shown;
    if (!before) return undefined;
    return { cars: [...before.state.grid, ...before.state.retired], teams: race.teams };
  }, [race, tableTarget, shown]);

  const flag = useMemo(() => {
    if (!race || !shown) return 'pre-race' as const;
    return flagFor({
      started: cursor > 0,
      finished: cursor >= race.result.turnsRun,
      yellowTurnsRemaining: shown.state.yellowTurnsRemaining,
      nextTurn: shown.state.turn,
      scheduledTurns: race.result.scheduledTurns,
    });
  }, [race, shown, cursor]);

  /**
   * THE MONEY AN EXHIBITION RACE MOVED.
   *
   * Run once, when the race is set up, and only read from here on — the same
   * discipline the race tape follows. A single exhibition race has no
   * championship, so no standings are passed: the points-leader contingency
   * does not pay and nobody carries a driver rating, which is the honest
   * answer rather than a made-up one (see `RaceFinanceInput.standings`).
   *
   * A SEASON RACE DOES NOT COME THROUGH HERE AT ALL. Its money was settled by
   * the season's own finance track, against the year's sponsorships and last
   * week's gate, and it arrives on `race.seasonFinance`. Pricing it a second
   * time off fresh books would put two different purses on one race.
   */
  const finance = useMemo(() => {
    if (!race || race.seasonFinance) return null;
    try {
      const profiles = race.result.finishingOrder.map((f) => {
        const card =
          race.week.grid.find((c) => c.driverId === f.driverId) ??
          race.result.state.grid.find((c) => c.card.driverId === f.driverId)?.card ??
          race.result.state.retired.find((c) => c.card.driverId === f.driverId)?.card;
        // SAG/12 — "A Team can have only one Driver." Our three-car shops are
        // his TEAM OWNERS; each car keeps its own books (D-029). Passing the
        // shop id here would let one team's winnings cover another car's costs,
        // which is the exact sentence the guide forbids.
        return card ? profileFromCard(card, financeTeamId(card.driverId)) : null;
      });
      const clean = profiles.filter((p): p is NonNullable<typeof p> => p !== null);
      return runRaceFinance({
        /**
         * ONE RACE'S MONEY, LABELLED FOR WHAT IT IS.
         *
         * A race started from the season option is race N of a real calendar,
         * and `raceNumber` is not decoration to the finance module — gate
         * attendance and the season's test races both move with it. So the
         * scheduled number is passed when there is one, and the id says which
         * kind of race paid out.
         *
         * ⚠ IT IS STILL A STANDING START FINANCIALLY. `newFinanceState` opens
         * fresh books and no standings are passed, so the points-leader
         * contingency does not pay and nothing accumulates across races. This
         * is one race's purse, not a season ledger, whichever mode produced it.
         */
        seasonId: race.config.seasonRaceNumber
          ? `season-opener-${race.config.seed}`
          : `exhibition-${race.config.seed}`,
        raceNumber: race.config.seasonRaceNumber ?? 1,
        seed: race.config.seed ^ 0x00f1_1a5c,
        track: race.result.track,
        week: race.week,
        race: race.result,
        profiles: clean,
        state: newFinanceState(clean),
      });
    } catch {
      return null;
    }
  }, [race]);

  const spotter = useMemo(() => {
    if (!shown?.turn) return null;
    const events = shown.turn.events;
    for (let i = events.length - 1; i >= 0; i--) {
      if (events[i].eventType !== 'race-cam') return events[i];
    }
    return events[events.length - 1] ?? null;
  }, [shown]);

  /**
   * D-EZ-73 (F5) — WHERE THE PLAYER IS, folded once for the strip.
   *
   * ⚠ THE LIVE SEASON WINS OVER THE SETTLED ONE. A player fourteen races into
   * season 3 also has a settled season 2 on the workbench, and a strip that
   * reported the settled one would tell him he is somewhere he left months ago.
   */
  const stripWhere = useMemo(() => {
    if (season) {
      const run = season.state.nextRace - 1;
      const next = season.state.schedule.races[season.state.nextRace - 1] ?? null;
      return {
        seasonNumber: season.seasonNumber,
        racesRun: run,
        nextRace: next ? next.raceName : null,
      };
    }
    if (reachableCareer) {
      return {
        seasonNumber: reachableCareer.seasonNumber,
        racesRun: reachableCareer.season.races.length,
        nextRace: null,
      };
    }
    return { seasonNumber: null, racesRun: null, nextRace: null };
  }, [season, reachableCareer]);

  /** Every settled season a strip chip can open, ascending. */
  const stripSeasons = useMemo(
    () =>
      [...reachableHistory, ...(reachableCareer ? [reachableCareer] : [])]
        .map((c) => c.seasonNumber)
        .sort((a, b) => a - b),
    [reachableHistory, reachableCareer],
  );

  /**
   * THE SEASON'S FEUDS, FOR THE BOOTH — D-EZ-74.
   *
   * `feudRead.ts` can already tell the booth when a grudge started TODAY and how
   * many times these two have found each other, because that is all in the race
   * it is calling. What it cannot know from a `RaceResult` is that they have
   * been at it since Bristol in race nine — `FeudEntry` is `{ a, b }` and the
   * answer lives in the SEASON's event stream, which `feudHistory.ts` folds.
   *
   * This is the join, done here because `App` is the only place that holds both
   * the race being watched and the season it belongs to. `SeasonPlay.tsx` and
   * `RaceSetup.tsx` each build the same thing for the dossier chevron; the
   * booth is the third caller and the widest, since it wants every pair on the
   * list rather than the one a card is open on.
   *
   * ⚠ STRICTLY EARLIER RACES. `season.state.events` is append-only and by the
   * time a race is being watched its own events may already be in there. Left
   * unfiltered, a beat on turn 5 could be handed a history whose origin is turn
   * 30 of the race still on the tape — narration knowing a result before it
   * happened, which is the one thing D-003 forbids outright. The week's own
   * ledger IS passed, because race week finishes before the green flag and a
   * feud struck in qualifying is legitimately known.
   *
   * `undefined` for an exhibition, or a race with nobody feuding, and the booth
   * then behaves exactly as it did before any of this existed.
   */
  const boothFeuds = useMemo(() => {
    if (!race || !season) return undefined;
    const pairs = race.result.state.feuds;
    if (pairs.length === 0) return undefined;
    const here = race.config.seasonRaceNumber ?? null;
    const earlier =
      here === null
        ? season.state.events
        : season.state.events.filter((e) => e.raceNumber < here);
    return [
      ...feudHistories(pairs, {
        season: earlier,
        week: race.week.events,
        weekRaceNumber: here,
        weekRaceName: race.result.state.raceName,
        raceNames: new Map<number, string>(
          season.state.races.map((r) => [r.raceNumber, r.scheduled.raceName]),
        ),
      }).values(),
    ];
  }, [race, season]);

  /** D-EZ-103 — the league's rules for the Settings panel: this season's, read-only, and the next one's. */
  const leagueView: LeagueSettingsView | null = useMemo(() => {
    const at = season?.seasonNumber ?? reachableCareer?.seasonNumber ?? null;
    if (at === null) return null;
    return {
      leagueName: leagueName || 'Your league',
      current: season ? settingsOfSeasonOptions(season.state.options) : settingsOfCareer(reachableCareer),
      currentLabel: season
        ? `Season ${season.seasonNumber} is being raced under these rules.`
        : `Season ${at} was raced under these rules.`,
      next: nextSettings,
      nextSeasonNumber: at + 1,
      onNextChange: setNextSettings,
    };
  }, [season, reachableCareer, leagueName, nextSettings]);

  /** D-EZ-103 — one line about how the last settled season ended, for the next-season card. */
  const settledLine = useMemo(() => {
    const last = reachableCareer;
    if (!last) return null;
    const id = last.season.championId;
    const name = id ? (last.season.finalStandings.byDriver[id]?.name ?? null) : null;
    return name ? `${name} won the championship.` : null;
  }, [reachableCareer]);

  /** D-EZ-103 — a fresh exhibition; `startAtWeek` rolls race week at once. */
  const newExhibition = useCallback((trackId: string, startAtWeek: boolean) => {
    setConfig((c) => ({
      ...c,
      trackId,
      totalTurns: trackId === c.trackId ? c.totalTurns : trackCard(trackId).turns,
      seed: randomSeed(),
    }));
    setExhibition((e) => ({ key: e.key + 1, startAtWeek }));
    setPlaying(false);
    setHome('exhibition');
    setView('setup');
  }, []);

  const handRolling = Boolean(handDice || seasonDice);
  /** No league on the workbench, and the player is at the front door. */
  /** No league on the workbench at all (front door, league creation or an exhibition). */
  const noLeague = !season && !reachableCareer && !loaded;

  /*
    D-EZ-102 — THE LEAGUE BEHIND THE RACE ON THE TAPE. What the league knew
    going into it (the booth's memory: built off the season as it stood BEFORE
    this race), the league's seasons (the Tracks tab), the big turns for the TV
    cutaway, and Victory Lane's view of the league.
  */
  const leagueAll = useMemo(
    () => leagueSeasons({ history: reachableHistory, career: reachableCareer, live: season }),
    [reachableHistory, reachableCareer, season],
  );
  const raceBigTurns = useMemo(() => (race ? bigTurns(race.result) : undefined), [race]);
  const victoryLeague = useMemo(
    () =>
      race && season && race.config.seasonRaceNumber
        ? { shelf: { history: reachableHistory, career: reachableCareer, live: season }, raceNumber: race.config.seasonRaceNumber }
        : null,
    [race, season, reachableHistory, reachableCareer],
  );

  return (
    <div className="app">
      <nav className="topbar" aria-label="Sections">
        <span className="topbar__mark"><span>RACING <b>SLOP</b></span><small>STOCK CAR TABLETOP · RWBR</small></span>
        <button
          type="button"
          className={`tab${view === 'setup' ? ' is-on' : ''}`}
          aria-current={view === 'setup' ? 'page' : undefined}
          onClick={() => setView('setup')}
        >
          Home
        </button>
        {/*
          D-EZ-73 (F2) — WATCH, AND IT SAYS WHY IT IS SHUT.

          It was "The race", one tab along from "Race week": two tabs, the same
          noun, and the difference between them a concept a new player has not
          learned yet. It is the tape — you watch it — and a disabled tab with
          no explanation is a question every visitor has to ask somebody.
        */}
        <button
          type="button"
          className={`tab${view === 'race' ? ' is-on' : ''}`}
          aria-current={view === 'race' ? 'page' : undefined}
          onClick={() => setView('race')}
          disabled={!race}
          title={race ? undefined : 'Drop the green on Home and the race plays here.'}
        >
          Watch
        </button>
        {/*
          D-EZ-103 — THE WAY BACK TO A HAND-ROLLED RACE. The dice table had no tab,
          so a player who looked at another screen mid-race had no way back to the
          race he was rolling. The table stays mounted while he is away.
        */}
        {handRolling && (
          <button
            type="button"
            className={`tab tab--dice${view === 'dice' ? ' is-on' : ''}`}
            aria-current={view === 'dice' ? 'page' : undefined}
            onClick={() => setView('dice')}
          >
            {handProgress ? `Your dice · turn ${handProgress.turn}` : 'Your dice'}
          </button>
        )}
        <button
          type="button"
          className={`tab${view === 'career' ? ' is-on' : ''}`}
          aria-current={view === 'career' ? 'page' : undefined}
          onClick={() => setView('career')}
        >
          Career
        </button>
        <button
          type="button"
          className={`tab${view === 'print' ? ' is-on' : ''}`}
          aria-current={view === 'print' ? 'page' : undefined}
          onClick={() => setView('print')}
        >
          Record book
        </button>
        {/* "Track cards" was our word for it. It is the tracks. */}
        <button
          type="button"
          className={`tab${view === 'content' ? ' is-on' : ''}`}
          aria-current={view === 'content' ? 'page' : undefined}
          onClick={() => setView('content')}
        >
          Tracks
        </button>
        {/*
          THE ART LIBRARY TAB — the owner's, and nobody else's.

          Not rendered disabled, and not rendered with an explanation: a
          visitor who cannot tag art has no use for the knowledge that a
          tagging screen exists, and a greyed-out tab is a question every one
          of them would have to ask. `ownerHere` is false while boot is still
          reading the session, so the tab appears a beat after a page load
          rather than flashing for everyone and then vanishing.
        */}
        {ownerHere && (
          <button
            type="button"
            className={`tab${view === 'art' ? ' is-on' : ''}`}
            aria-current={view === 'art' ? 'page' : undefined}
            onClick={() => setView('art')}
          >
            Art library
          </button>
        )}
        {ownerHere && (
          <button
            type="button"
            className={`tab${view === 'logos' ? ' is-on' : ''}`}
            aria-current={view === 'logos' ? 'page' : undefined}
            onClick={() => setView('logos')}
          >
            Marks
          </button>
        )}
        {/*
          THE ADMIN TAB — D-EZ-80, and the same rule as the two above it: an
          owner-only tab is not rendered disabled and not explained. A visitor
          who cannot read the account list has no use for knowing one exists.

          ⚠ `ownerHere` DECIDES WHETHER TO DRAW IT AND NOTHING ELSE. Every route
          the panel calls is gated in the Worker against a token Supabase itself
          resolves (`worker/owner.ts`), so this line is a courtesy to the person
          holding the mouse and worth nothing to anyone who opens devtools.
        */}
        {ownerHere && (
          <button
            type="button"
            className={`tab${view === 'people' ? ' is-on' : ''}`}
            aria-current={view === 'people' ? 'page' : undefined}
            onClick={() => setView('people')}
          >
            People
          </button>
        )}
      </nav>

      {/*
        THE ACCOUNT BAR — in the shell, so it is reachable from every screen.

        It sits UNDER the tab bar rather than inside it: the tab strip is a
        46px row of single words and the bar is a form with two inputs and
        three buttons, and folding one into the other would break both.
        "Reachable from every screen" is what the requirement asks for, and a
        strip that is always the second thing on the page satisfies it.

        NOT A DOOR. Nothing below it is gated on being signed in. A visitor who
        ignores this entirely can set up a race week, run every race, read the
        booth, open a dossier and run a full career season — and loses exactly
        one thing, which is coming back to the same league tomorrow.
      */}
      {/*
        D-EZ-73 (F5, F6) — THE STRIP THAT SAYS WHERE YOU ARE.

        The account bar and the model panel used to stand here permanently: a
        form and a heading, on every screen, for two settings a player touches
        once. They are behind labelled buttons now and this line has their room.

        ⚠ THE NUDGE STILL WORKS. `flagAccount` is what "take me to the cloud
        slot" sets, and a panel that is closed when the nudge fires is a nudge
        that lands on nothing — so the flag forces it open as well as lighting
        it.
      */}
      <SeasonStrip
        leagueName={leagueName}
        seasonNumber={stripWhere.seasonNumber}
        racesRun={stripWhere.racesRun}
        racesInSeason={SEASON_RACES}
        nextRace={stripWhere.nextRace}
        watching={view === 'race' && race !== null}
        seasons={stripSeasons}
        openSeason={bookSeason}
        onPickSeason={(n) => {
          setBookSeason(n);
          setView('print');
        }}
      >
        <button
          type="button"
          className={`sstrip__tool${shellPanel === 'account' ? ' is-on' : ''}`}
          aria-expanded={shellPanel === 'account'}
          onClick={() => setShellPanel((p) => (p === 'account' ? null : 'account'))}
        >
          Account
        </button>
        <button
          type="button"
          className={`sstrip__tool${shellPanel === 'ai' ? ' is-on' : ''}`}
          aria-expanded={shellPanel === 'ai'}
          onClick={() => setShellPanel((p) => (p === 'ai' ? null : 'ai'))}
        >
          Commentary
        </button>
        {/* D-EZ-101 — display preferences (rulebook citations). */}
        <button
          type="button"
          className={`sstrip__tool${shellPanel === 'settings' ? ' is-on' : ''}`}
          aria-expanded={shellPanel === 'settings'}
          onClick={() => setShellPanel((p) => (p === 'settings' ? null : 'settings'))}
        >
          Settings
        </button>
      </SeasonStrip>

      {shellPanel === 'settings' && (
        <div className="shellbar">
          <SettingsPanel league={leagueView} />
        </div>
      )}

      {/*
        D-EZ-48 / D-EZ-100 — COMMENTARY, ITS OWN PANEL. It used to sit inside the
        Account panel's bar, which is shut unless Account is open — so pressing
        Commentary could show nothing at all.
      */}
      {shellPanel === 'ai' && (
        <div className="shellbar">
          <AiKeyPanel serverHasKey={ownerHere} startOpen />
        </div>
      )}

      <div
        ref={accountRef}
        className={`shellbar${flagAccount ? ' is-flagged' : ''}${
          shellPanel === 'account' || flagAccount ? '' : ' is-shut'
        }`}
      >
        {/*
          BOTH THINGS THAT CAN BE SAVED ARE PASSED IN, and the bar decides which
          one Save takes (the live season, when there is one — see its header).
          Passing only the career was the bug: a player fourteen races into a
          season had a bar that could see nothing to save, and said so.
        */}
        <AccountBar
          career={reachableCareer}
          /* D-EZ-65 — the whole shelf goes in the save, not just the workbench. */
          history={reachableHistory}
          season={season}
          leagueId={leagueId}
          leagueName={leagueName}
          onLoad={onLeagueLoaded}
          onSaved={noteSaved}
          backupTick={backupTick}
          onDeviceChange={() => setBackupTick((t) => t + 1)}
          settings={nextSettings}
        />

        {/*
          WHAT CAME BACK OUT OF THE SLOT, and the one thing it can be used for.

          Terse on purpose, and the button is the point: a load that reported
          five counts and offered nothing to do with them was the dead end
          Howard reported. The second sentence is the honest limit — one line,
          not a paragraph — because a saved slot does not contain a played
          season's fold, finance or winter.
        */}
        {loaded && (
          <div className="shellbar__note shellbar__note--ok">
            <p>
              <b>
                Loaded {loaded.leagueName} — season {loaded.seasonNumber}.
              </b>{' '}
              {loaded.carry.roster.length} drivers and {loaded.carry.teams.length} teams carried out
              of the winter.
            </p>
            <div className="shellbar__row">
              <button
                type="button"
                className="btn btn--hot"
                onClick={() => continueLoaded(loaded)}
              >
                Continue — play season {loaded.seasonNumber + 1} race by race
              </button>
              {/*
                ⚠ TWO DIFFERENT TRUTHS NOW — D-EZ-65. A file written since the
                shelf landed carries the seasons themselves, so the sentence that
                used to be unconditionally true is true only of the old ones. A
                stale apology is worse than none: it tells a player something is
                missing while it sits on the Record Book page.
              */}
              <span className="dim small">
                {loaded.archive.length > 0 ? (
                  <>
                    {loaded.archive.length === 1
                      ? `Season ${loaded.archive[0].seasonNumber} is`
                      : `${loaded.archive.length} seasons are`}{' '}
                    on the Record Book and the Career page — standings, money, the winter and every
                    career total. The turn-by-turn race logs are not kept.
                  </>
                ) : (
                  <>
                    Season {loaded.seasonNumber} itself cannot be reopened: this file was written
                    before the seasons were saved with it, so it carries the roster, the races and
                    the money books but not the winter that settled them. Save again and it will.
                  </>
                )}
              </span>
            </div>
          </div>
        )}
      </div>

      {/*
        THE CLOUD NUDGE — Howard, 17 Aug 2026: "upon opening the game on a new
        location they must be encouraged strongly to download from the cloud
        first." It is above the content and below the bar it points at, and it
        is hidden by KIND once dismissed, so a different situation — one where
        loading would now destroy something — brings a new banner rather than
        being silenced by an earlier dismissal.
      */}
      {/*
        D-EZ-103 — never over a race or the dice, and at the front door only when
        it matters (a saved league to load, or a real warning) — a first visitor
        is not told about a league they have not started.
      */}
      {nudge &&
        !nudgeOff &&
        nudge.kind !== dismissedNudge &&
        view !== 'race' &&
        view !== 'dice' &&
        shellPanel !== 'account' &&
        (!noLeague || nudge.kind === 'load-first' || nudge.level === 'danger') && (
        <CloudNudge
          decision={nudge}
          onShowAccount={showAccount}
          onDismiss={() => {
            setDismissedNudge(nudge.kind);
            setNudgeOff(true);
            try {
              window.sessionStorage.setItem('rwbr.cloudstrip.dismissed', '1');
            } catch {
              /* the dismissal still holds for this page */
            }
          }}
        />
      )}

      <ErrorNote error={error} className="err wrap" />

      {/*
        RACE WEEK — one screen, two modes, and the season is one of them.

        `seasonPanel` is the whole of race-by-race play: when a season is live it
        replaces the exhibition form in place, so "Season" on this tab is a
        running championship rather than a warning about one. When none is live,
        the same switch offers to start one and hands the seed up to
        `beginCareer`.
      */}
      {/*
        HOME — D-EZ-103. One place a season starts and is played:

          • a live season   → the season home (`SeasonPlay`): play the next race,
                              or simulate the rest of the year;
          • between seasons → the next-season card;
          • no league       → the front door, league creation or an exhibition.
      */}
      {view === 'setup' && (
        <main className="wrap">
          {season ? (
            <SeasonPlay
              state={season.state}
              leagueName={leagueName}
              seasonNumber={season.seasonNumber}
              teams={season.teams}
              onGreen={(step) => {
                setRolledWeekend(null);
                takeGreen(step);
              }}
              onHandRoll={handRollSeasonRace}
              handRolledStep={rolledWeekend}
              /*
                D-EZ-67 — so a driver card opened from the starting grid can
                see the years behind this one.
              */
              career={reachableCareer}
              history={reachableHistory}
              onSettle={settle}
              settling={settling}
              onLeave={() => setSeason(null)}
              onSimulate={simulateAhead}
              simulating={fastForward}
            />
          ) : home === 'create' ? (
            <LeagueCreate
              initialSeed={config.seed}
              onCreate={createLeague}
              onBack={() => setHome('door')}
              replacing={reachableCareer || loaded ? leagueName || 'your league' : null}
            />
          ) : home === 'exhibition' ? (
            <RaceSetupScreen
              key={exhibition.key}
              initial={config}
              onStart={start}
              startAtWeek={exhibition.startAtWeek}
              onBack={() => setHome('door')}
              note={<NamePoolNote pool={pool} />}
            />
          ) : loaded || reachableCareer ? (
            <>
            <NextSeasonCard
              leagueName={leagueName || 'Your league'}
              seasonNumber={(loaded ?? reachableCareer)!.seasonNumber}
              championLine={settledLine}
              onPlay={() => (loaded ? continueLoaded(loaded) : reachableCareer && continueCareer(reachableCareer))}
              onSimulate={() =>
                loaded ? continueLoaded(loaded, true) : reachableCareer && continueCareer(reachableCareer, true)
              }
              onReadLeague={() => setView('career')}
              onNewLeague={() => setHome('create')}
              busy={Boolean(fastForward) || settling}
            />
            {/* D-EZ-110 — the winter between, told: Silly Season, rookies, Hall of Fame night, the preview. */}
            <WinterChapters
              seasons={loaded ? loaded.archive : [...reachableHistory, ...(reachableCareer ? [reachableCareer] : [])]}
              seasonNumber={(loaded ?? reachableCareer)!.seasonNumber}
            />
            </>
          ) : (
            <FrontDoor
              onNewLeague={() => setHome('create')}
              onExhibition={() => setHome('exhibition')}
              onLoad={showAccount}
              resume={
                deviceLeague
                  ? { label: describeBackup(deviceLeague), onResume: resumeDeviceLeague, error: resumeError }
                  : null
              }
            />
          )}
        </main>
      )}

      {/*
        D-018 — the hand-rolled race.

        Keyed on the setup so that starting a second hand-rolled race gets a
        FRESH session rather than one carrying the first race's faces. Nothing
        else on the page is torn down: the tabs, the account bar and the art
        library are all still there, because a race that takes an evening to
        roll should not hold the rest of the app hostage.
      */}
      {handDice && (
        /* D-EZ-103 — mounted while the player looks elsewhere: the faces live here. */
        <main className="wrap" style={view === 'dice' ? undefined : { display: 'none' }}>
          <DiceTable
            key={`${handDice.setup.seed}-${handDice.setup.raceId ?? handDice.setup.raceName ?? ''}`}
            setup={handDice.setup}
            options={handDice.options}
            active={view === 'dice'}
            onProgress={setHandProgress}
            onFinished={(result) => {
              setHandDice(null);
              setHandProgress(null);
              handDice.complete(result);
            }}
            onCancel={() => {
              setHandDice(null);
              setHandProgress(null);
              setView('setup');
            }}
          />
        </main>
      )}

      {/*
        D-018 for a SEASON WEEKEND. The same table, driven by `ManualSeasonRace`,
        which owns race N of the live season: the player's faces feed the RACE
        WEEK first — qualifying, the TV list, the feuds — and then the race that
        follows it, off one list, split where the week actually stopped. What
        comes back is a finished weekend, handed to `SeasonPlay` to be told.
      */}
      {!handDice && seasonDice && (
        <main className="wrap" style={view === 'dice' ? undefined : { display: 'none' }}>
          <DiceTable
            key={`season-${seasonDice.state.seasonId}-${seasonDice.raceNumber}`}
            active={view === 'dice'}
            onProgress={setHandProgress}
            season={{
              state: seasonDice.state,
              onFinished: (step) => {
                setSeasonDice(null);
                setHandProgress(null);
                // Back to the season screen, NOT to the tape. The player has
                // just rolled a race week he has not been shown — the grid, the
                // TV list and the feuds are his, and skipping them here would
                // throw away the half of the weekend he sat and rolled.
                setRolledWeekend(step);
                setView('setup');
              },
            }}
            onCancel={() => {
              setSeasonDice(null);
              setHandProgress(null);
              setView('setup');
            }}
          />
        </main>
      )}

      {/* Gated on the same value as the tab, not just on `view`: the effect
          above moves a non-owner off this screen, and a render between the
          account changing and that effect running must not draw the tagger. */}
      {/* The marks cabinet. Gated on the same value as its tab, for the same
          reason the art library is: a render between the account changing and
          the effect above running must not draw an owner-only screen. */}
      {view === 'logos' && ownerHere && (
        <main className="wrap">
          <Suspense fallback={loadingScreen}>
            <LogoManager teams={season?.teams ?? []} />
          </Suspense>
        </main>
      )}

      {view === 'art' && ownerHere && (
        <main className="wrap">
          <Suspense fallback={loadingScreen}>
            <ArtTagger />
          </Suspense>
        </main>
      )}

      {/*
        THE CAREER TAB — a record, not a container. Every piece of state it used
        to own is passed in and handed back, which is what makes the same season
        visible to Save, to the record book and to the race-by-race screen.
      */}
      {view === 'career' && (
        /*
          WIDER THAN THE REST OF THE APP, on purpose. The book's race grid is
          thirty-six columns across; at the standard 1160px it would scroll
          sideways on a desktop for no reason but a stylesheet.
        */
        <main className="wrap wrap--book">
          <CareerScreen
            career={reachableCareer}
            history={reachableHistory}
            leagueName={leagueName}
            seasonInPlay={
              season ? { seasonNumber: season.seasonNumber, state: season.state } : null
            }
            /*
              THE LIVE SEASON, WHOLE — not just how far it has got. The career page
              draws the running championship off this.
            */
            live={season}
            /* D-EZ-103 — seasons are started and simulated on Home, read here. */
            onPlayRaceByRace={() => setView('setup')}
            onPlayNextSeason={reachableCareer ? () => { setHome('door'); setView('setup'); } : undefined}
          />
        </main>
      )}

      {/*
        UI-014 — THE RECORD BOOK, as a view of its own.

        It is top-level rather than a panel on the career screen for two
        reasons: a record book is a document, and `PrintSheet` hides everything
        that is not itself when the printer runs (`print.css`, `@media print`),
        which is a thing a whole page may do and a panel may not.
      */}
      {view === 'print' && (
        <main className="wrap">
          {/*
            D-EZ-66 — THE HOLE, AND THE OFFER TO FILL IT.

            Rendered above the book and in the empty state alike, because the
            league that most needs it is the one with NOTHING behind the live
            season — which is exactly the case where there is no book to hang a
            panel inside. It draws nothing at all when the shelf is complete.

            The carry is the live season's own starting roster and garage, which
            is what the rebuild is checked against. `seasonsRun` is on the season
            when it was carried out of a winter and absent when it was not; the
            check reports which.
          */}
          {season && season.seasonNumber > 1 && (
            <RebuildSeasons
              liveSeasonNumber={season.seasonNumber}
              liveSeed={season.state.seed}
              carry={{
                roster: season.roster,
                teams: season.teams,
                seasonsRun: season.seasonsRun ?? null,
              }}
              have={[...reachableHistory, ...(reachableCareer ? [reachableCareer] : [])].map(
                (s) => s.seasonNumber,
              )}
              pool={poolRef.current?.pool ?? null}
              onRebuilt={(seasons) => {
                /*
                  ⚠ THE REBUILT SEASONS ARE MERGED, NOT SUBSTITUTED. A league can
                  hold some of its history already — a season settled in this
                  browser since the load — and replacing the shelf wholesale
                  would throw that away to put an older copy of it back.
                */
                const byNumber = new Map<number, CareerSeason>();
                for (const s of [...seasons, ...reachableHistory]) byNumber.set(s.seasonNumber, s);
                if (reachableCareer) byNumber.set(reachableCareer.seasonNumber, reachableCareer);
                const shelf = [...byNumber.values()].sort((a, b) => a.seasonNumber - b.seasonNumber);
                setReachableCareer(shelf[shelf.length - 1] ?? null);
                setReachableHistory(shelf.slice(0, -1));
                noteLocalWork(
                  `${shelf.length} rebuilt season${shelf.length === 1 ? '' : 's'} on the workbench, not yet saved`,
                );
              }}
            />
          )}
          {reachableCareer ? (
            <Suspense fallback={loadingScreen}>
            <PrintSheet
              career={reachableCareer}
              history={reachableHistory}
              /*
                D-EZ-63 — every settled season this browser can reach, so the
                book has a shelf rather than one volume. `history` already holds
                the earlier ones; passing them here as well is what turns them
                into chips instead of leaving them as a career total nobody can
                open.
              */
              seasons={[...reachableHistory, reachableCareer]}
              leagueName={leagueName}
              /* F6 — the shell strip's chips decide which season this opens at. */
              openSeason={bookSeason}
              onClose={() => setView('career')}
            />
            </Suspense>
          ) : (
            /*
              AN HONEST EMPTY STATE, AND A SHORT ONE. It prints a season that was
              run; none has been. The two ways to get one are buttons, not
              paragraphs. `season` being live is the interesting case: the record
              book of an unfinished year does not exist, and saying which race it
              is up to is more use than explaining why.
            */
            <section className="emptystate" aria-labelledby="print-empty-h">
              <p className="kicker">Record book</p>
              <h2 id="print-empty-h">No finished season to print</h2>
              {season ? (
                <p>
                  Season {season.seasonNumber} is {season.state.nextRace - 1} of {SEASON_RACES} races
                  in. The book prints when the year is settled.
                </p>
              ) : (
                <p>Finish a season — raced or simulated — and it prints from here.</p>
              )}
              <button
                type="button"
                className="btn btn--hot"
                onClick={() => {
                  if (!season) setHome('door');
                  setView('setup');
                }}
              >
                {season ? 'Back to the season' : 'Go to Home'}
              </button>
            </section>
          )}
        </main>
      )}

      {view === 'content' && (
        <main className="wrap">
          {/* D-EZ-102 — track histories from the league's seasons. */}
          <TrackContent seasons={leagueAll} />
        </main>
      )}

      {/*
        ⚠ `ownerHere &&` IS REPEATED HERE AND NOT ONLY ON THE TAB. The tab is how
        you get here; a stale `view` is another. `setView('people')` survives a
        sign-out, so without this the panel would keep rendering — 401ing on
        every call, but rendering — for somebody who had just signed out of it.
      */}
      {view === 'people' && ownerHere && (
        <main className="wrap">
          <Suspense fallback={loadingScreen}>
            <AdminPanel />
          </Suspense>
        </main>
      )}

      {view === 'race' && race && shown && (
        <main className="wrap wrap--race">
          <TurnControls
            raceName={race.result.state.raceName}
            track={race.result.track}
            raceKey={
              season && race.config.seasonRaceNumber
                ? (season.state.schedule.races[race.config.seasonRaceNumber - 1]?.raceKey ?? null)
                : null
            }
            flag={flag}
            nextTurn={shown.state.turn}
            scheduledTurns={race.result.scheduledTurns}
            turnsRun={race.result.turnsRun}
            cursor={cursor}
            turn={shown.turn}
            pitTurns={shown.state.pitTurns}
            playing={playing}
            pace={pace}
            onPaceChange={changePace}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onToTheFlag={toTheFlag}
            onRewind={rewind}
            onStepBack={stepBack}
            /* D-EZ-103 — an exhibition only; a season race belongs to the championship. */
            onNewRace={
              race.config.seasonRaceNumber
                ? undefined
                : () => {
                    setPlaying(false);
                    setHome('exhibition');
                    setView('setup');
                  }
            }
          />

          {/*
            WHO NAMED THIS FIELD. Counted, not claimed — `renamed` and
            `keptBuiltIn` come straight off `applyNamePool`, and a field that is
            part pool and part built-in says both numbers rather than rounding
            itself up to "AI names". Trimmed to the counts: the reasoning that
            used to be printed here is in `nameStore.ts` and in `beginCareer`.
          */}
          {naming && (
            <p className="namebar" role="status">
              <b className="namebar__k">Driver names</b>
              {naming.renamed > 0 ? (
                <>
                  {naming.renamed} new names for this league
                  {naming.keptBuiltIn > 0 && <> · {naming.keptBuiltIn} from the classic list</>}
                  <Cite>{naming.shortfall > 0 && <> · {naming.shortfall} beyond the pool</>}</Cite>
                </>
              ) : (
                <>All {naming.keptBuiltIn} drivers carry the classic names.</>
              )}
            </p>
          )}

          {/*
            D-EZ-73 (F3) — THE BOARDS COME FIRST.

            From the design review, 20 Aug 2026:

              "The screen opens with the booth, the dice list and the race-cam
               photo across the top — three panels of very different content —
               then a full-width notice, then the boards. The boards are the
               game. They're the fourth thing down and the first thing a player
               scrolls to find, every single turn."

            The broadcast row was itself a fix for the same problem — the three
            panels used to STACK, which was worse — but a third of the width
            each still put a band of supporting material between the controls
            and the subject. They are a rail beside the boards now.

            ⚠ THE SPOTTER MOVED IN WITH THE BOARDS AND THAT IS THE POINT. It is
            a caption for the running order — "cars off the board: 2 open
            spaces" — and it was floating at full width between two sections,
            describing a thing that was not on screen with it.
          */}
          {/*
            D-EZ-103 — BIG MOMENTS, SAID OUT LOUD. The race cam call for the
            next turn is put on screen before the turn is decided, and a big
            moment names itself while it rolls on the table.
          */}
          {/*
            D-EZ-105 — THE CALL. After a commissioner throw the result stays up
            in one bold line while the board moves.
          */}
          {pace === 'commissioner' && !tableSession && calls.length > 0 && calls[calls.length - 1].turn === cursor ? (
            <p className="camcall camcall--big camcall--call" role="status" aria-live="polite">
              <b>Turn {calls[calls.length - 1].turn}</b> {calls[calls.length - 1].line}
            </p>
          ) : (pace === 'moments' || pace === 'commissioner') && (moment && tableSession?.target === moment.turn ? (
            <p className="camcall camcall--big" role="status" aria-live="polite">
              <b>{pace === 'commissioner' ? 'Your table' : 'Big moment'} · turn {moment.turn}</b> {MOMENT_WORDS[moment.reason]} —{' '}
              {pace === 'commissioner' ? 'the big throws are yours.' : 'rolling it on the table.'}
            </p>
          ) : peek && peek.turn === cursor + 1 && peek.call ? (
            <p className="camcall" role="status" aria-live="polite">
              <b>Race cam · turn {peek.turn}</b> {peek.call.line}
            </p>
          ) : null)}
          <div className="racemain">
            <div className="racecast" aria-label="Current race action">
              {/* D-EZ-102 — the radio and, under it, the TV cutaway (back on screen). */}
              <div className="racecast__radio">
              {/* D-EZ-105 — the commissioner's throws go out on the radio. */}
              {pace === 'commissioner' && calls.length > 0 && (
                <section className="calls" aria-label="Your throws this race">
                  <p className="calls__head">Your throws · {calls.length}</p>
                  <ol className="calls__list">
                    {calls.slice(-3).reverse().map((c) => (
                      <li key={c.key} className="calls__item">
                        <span className="calls__turn">T{c.turn}</span> <b>{c.line}</b>
                      </li>
                    ))}
                  </ol>
                </section>
              )}
              <BoothCall
                result={race.result}
                cursor={cursor}
                complete={cursor >= race.result.turnsRun}
                teams={race.teams}
                feudHistory={boothFeuds}
                raceNumber={race.config.seasonRaceNumber ?? undefined}
                season={boothSeason}
                league={boothLeague}
              />
              <MomentPanel
                library={artLibrary}
                events={race.result.events}
                cursor={cursor}
                trackCategory={race.result.track.category}
                seed={race.config.seed}
                condition={conditionForRace(race.result.track.id, race.result.state.raceName)}
                history={artHistory.current}
                flag={flag}
                onPitRoad={shown.state.pitTurns.includes(shown.state.turn - 1)}
                bigTurns={raceBigTurns}
              />
              </div>
              <RaceDiceWorkbench
                turn={shown.turn ?? undefined}
                pending={tableSession ? race.result.turns[tableSession.target - 1] : undefined}
                finish={cursor >= turnsRun ? drawsAtFinish(race.result) : undefined}
                pendingFinish={tableSession?.target === turnsRun ? drawsAtFinish(race.result) : undefined}
                session={tableSession}
                preferences={tablePreferences}
                onPreferences={setTablePreferences}
                onComplete={completeTableTurn}
                onRoll={advance}
                onReplay={() => beginTableTurn(cursor, true, true)}
                atEnd={cursor >= turnsRun}
                playing={playing}
                resultFor={(m) => (stakesFrame && tableSession ? afterLine(stakesFrame, tableSession.target, m) : '')}
                onCall={onCall}
                field={tableField}
              />
            </div>
            <div className="racemain__stage">
              {/* One plain true line for the turns before the booth's first
                  beat airs, and for any turn the director passed over. */}
              <p className="spotter" role="status" aria-live="polite">
                {spotter ? (
                  <>
                    <span className="spotter__line">{spotter.description}</span>
                    <Cite className="spotter__cite">{spotter.ruleCitation}</Cite>
                  </>
                ) : (
                  <span className="spotter__line">
                    Field is set. {race.result.state.grid.length + race.result.state.retired.length}{' '}
                    cars take the green.
                  </span>
                )}
              </p>

              {/* D-EZ-102 — the Big One, rain and red flags, at the turn they happen. */}
              <RaceMomentCard
                result={race.result}
                cursor={cursor}
                library={artLibrary}
                trackCategory={race.result.track.category}
                seed={race.config.seed}
              />

              <RaceBoards
                state={shown.state}
                previous={shown.previous}
                turn={shown.turn}
                highlighted={lit}
                flag={flag}
                teams={race.teams}
                onSelectDriver={setDossier}
              />
            </div>

            <details className="racejournal">
              {/* D-EZ-101 — one name for the race log, everywhere: "Race record". */}
              <summary>Race record</summary>
              <div className="racejournal__grid">
                <EventFeed
                  turns={race.result.turns}
                  cursor={cursor}
                  nameOf={(id) => race.result.finishingOrder.find((f) => f.driverId === id)?.name ?? race.week.grid.find((c) => c.driverId === id)?.name ?? id}
                  selected={selected}
                  onSelect={(selection, actors) => { setSelected(selection); setLit(actors); if (selection) scrubTo(selection.turn); }}
                />
                <div className="racejournal__extras">
                  <details className="tableledger"><summary>Race record · every dice roll</summary><TurnDice turns={race.result.turns} cursor={cursor} /></details>
                </div>
              </div>
            </details>
          </div>

          {cursor >= race.result.turnsRun && (
            <>
              {/* D-EZ-102 — Victory Lane, before the result sheet. */}
              <VictoryLane
                result={race.result}
                teams={race.teams}
                contracts={(race.seasonFinance ?? finance)?.state ?? null}
                league={victoryLeague}
                onReadPaper={
                  season && race.config.seasonRaceNumber
                    ? () => void openPaper({ seasonNumber: season.seasonNumber, raceNumber: race.config.seasonRaceNumber ?? undefined })
                    : undefined
                }
              />
              <RaceResults result={race.result} />
              {/* The season's own books when there is a season; the standing-start
                  purse only when there is not. Never both. */}
              {(race.seasonFinance ?? finance) && (
                <Payday
                  finance={(race.seasonFinance ?? finance)!}
                  nameOf={(id) =>
                    race.result.finishingOrder.find((f) => f.driverId === id)?.name ?? id
                  }
                />
              )}

              {/* D-EZ-103 — an exhibition ends on what to do next, not on a dead end. */}
              {!race.config.seasonRaceNumber && (
                <ExhibitionHandoff
                  raceName={race.result.state.raceName}
                  winnerName={race.result.finishingOrder[0]?.name ?? null}
                  onRunAgain={() => newExhibition(race.config.trackId, true)}
                  onPickTrack={() => newExhibition(race.config.trackId, false)}
                  onStartLeague={() => {
                    setPlaying(false);
                    setHome('create');
                    setView('setup');
                  }}
                />
              )}

              {/*
                THE WAY BACK INTO THE SEASON — the button Howard could not find.
                Race N is in the books and the state has already moved on, so this
                is a link to the next race week and not a second commit.
              */}
              {season && race.config.seasonRaceNumber && (
                <div className="rwhandoff">
                  <p className="kicker">
                    {leagueName} · season {season.seasonNumber}
                  </p>
                  <h2>
                    {isSeasonComplete(season.state)
                      ? `All ${SEASON_RACES} races are run`
                      : `Race ${race.config.seasonRaceNumber} of ${SEASON_RACES} is in the books`}
                  </h2>
                  <div className="rwhandoff__row">
                    <button
                      type="button"
                      className="btn btn--hot btn--big"
                      onClick={() => {
                        setPlaying(false);
                        setView('setup');
                      }}
                    >
                      {isSeasonComplete(season.state)
                        ? 'Settle the season'
                        : `Next race — race ${nextRaceNumber(season.state)} of ${SEASON_RACES}`}
                    </button>
                    {/*
                      The paper, on demand — and it is here whether or not the
                      modal opened by itself, because a player who turned the
                      auto-open off still has an edition waiting for them.
                    */}
                    <button
                      type="button"
                      className="btn btn--quiet"
                      onClick={() =>
                        void openPaper({
                          seasonNumber: season.seasonNumber,
                          raceNumber: race.config.seasonRaceNumber ?? undefined,
                        })
                      }
                    >
                      Read the paper
                    </button>
                    <button type="button" className="btn btn--quiet" onClick={() => setView('career')}>
                      The career
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/*
            UI-008 — THE DOSSIER, ON THE CURSOR.

            `cursor` is passed straight through, so the modal is a view of the
            same moment the boards are: rewind to turn 12 with it open and every
            number in it walks back to turn 12. It is not filtered to the
            cursor — `foldRace` slices the tape and never sees the rest, which
            is the difference between a rule and a reminder.
          */}
          {dossier && (
            <DriverDossier
              driverId={dossier}
              race={{
                raceName: race.result.state.raceName,
                start: race.start,
                turns: race.result.turns,
                cursor,
                teams: race.teams,
              }}
              /*
                D-EZ-60 — THE SEASON AND THE CAREER, FROM THE BOARDS.

                This call site handed in the race and nothing else, so a card
                opened mid-race told a player in his fourth championship season
                that he was running a one-off exhibition. The shell has always
                held the league; it simply was not passing it.
              */
              career={reachableCareer ?? undefined}
              history={reachableHistory}
              liveSeason={season}
              season={
                season
                  ? {
                      seasonNumber: season.seasonNumber,
                      cards: new Map(season.roster.map((c) => [c.driverId, c])),
                      teams: season.teams,
                      standings: season.state.standingsAfterRace.at(-1),
                      throughRace: season.state.nextRace - 1,
                      totalRaces: SEASON_RACES,
                    }
                  : undefined
              }
              onClose={() => setDossier(null)}
              onSelectDriver={setDossier}
            />
          )}
        </main>
      )}

      {/*
        THE PAPER — D-EZ-50. Top level, over whatever screen is behind it, and
        outside every view switch: an edition that vanished because the player
        changed tabs would be an edition they could not finish reading.
      */}
      {paper && (
        <PaperModal
          article={paper.article}
          loading={paper.loading}
          reason={paper.reason}
          standing={paper.standing}
          notes={paper.notes}
          volume={paper.volume}
          onClose={() => setPaper(null)}
          onRewrite={
            // Only offered when a model wrote it. Asking the game to write its
            // own article "again" would hand back the same deterministic words.
            paper.article?.source === 'ai' ? () => void openPaper(paper.of, true) : undefined
          }
        />
      )}
      <footer className="tabletop-footer"><span>RACING SLOP · THE STOCK CAR TABLETOP</span><span>Every roll has a story. Every result has a record.</span></footer>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * WHERE THE NAMES ON THE NEXT GRID WILL COME FROM — said before the player
 * reads a single one of them.
 *
 * Howard, 17 Aug 2026: *"are the fictional names the same every time? I dont
 * like that. Let the ai make a batch first and then changing the seed only
 * moves around those AI created names and doesnt remake more. Only the first
 * loading when someone visits the site without a career going."*
 *
 * ONE LINE, and it is the count. A pool of names either exists or it does not,
 * and the number is the whole of what a player can act on. The seam that used to
 * be explained here at length — an exhibition's field is renamed at the green,
 * so the entry list shows the built-in names — is still true of an EXHIBITION
 * and is no longer true of a SEASON, whose roster is named once by `beginCareer`
 * before the first card is dealt. When there is no pool, `EnsuredPool.detail`
 * says why in its own words and is printed verbatim.
 */
function NamePoolNote({ pool }: { pool: EnsuredPool | null }) {
  if (pool === null) {
    return (
      <p className="namenote" role="status">
        <b className="namenote__k">Driver names</b> Looking for a pool of written names.
      </p>
    );
  }

  const have = pool.pool;
  return (
    <p className={`namenote${have ? ' is-on' : ''}`} role="status">
      <b className="namenote__k">Driver names</b>
      {have ? (
        <>
          {have.names.length} names ready for this league
          <Cite>
            {have.model ? ` · ${have.model}` : ''} · a new seed reshuffles them
          </Cite>
        </>
      ) : (
        <>{pool.detail}</>
      )}
    </p>
  );
}
